import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { studioCasesAPI } from '../../studio-cases/api/studioCasesAPI';
import { examStudioAPI } from '../api/examStudioAPI';
import ExamReaderPanel from './ExamReaderPanel';
import { typesetMathJax } from './examReaderUtils';
import { sanitizeHtml } from '../../../lib/sanitizeHtml';

/** HTML from a paper, with its maths rendered.
 *
 * The page typesets only elements carrying mathjax-process, and nothing on
 * this screen asked MathJax to run, so solutions showed their LaTeX source.
 * This marks the content and typesets it, the same way ExamReaderPanel does.
 */
function StudioMathHtml({ html, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let cancelled = false;
    const run = () => {
      if (!cancelled) typesetMathJax(el);
    };
    run();
    const t1 = setTimeout(run, 200);
    const t2 = setTimeout(run, 500);
    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [html]);
  return (
    <div ref={ref}>
      <div
        className="studio-case-html-body mathjax-process"
        style={style}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
      />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--iv-bg)', color: 'var(--iv-body)', fontFamily: 'var(--iv-font-body)', lineHeight: 1.5 },
  inner: { maxWidth: 'min(1100px, calc(100vw - 32px))', margin: '0 auto', padding: '48px 24px 80px', width: '100%', boxSizing: 'border-box' },
  h1: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.02em', margin: '0 0 8px' },
  sub: { fontSize: '0.875rem', color: 'var(--iv-body)', marginBottom: 32 },
  label: { display: 'block', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, color: 'var(--iv-body)' },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid var(--iv-line)', padding: '10px 12px', fontSize: '0.9375rem', marginBottom: 16, background: 'var(--iv-raised)', color: 'var(--iv-head)', borderRadius: 0 },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--iv-line)',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    minHeight: 120,
    marginBottom: 16,
    background: 'var(--iv-raised)',
    color: 'var(--iv-body)',
    borderRadius: 0,
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: 'inherit',
    lineHeight: 1.5,
  },
  row: { display: 'flex', gap: 16, flexWrap: 'wrap' },
  rowItem: { flex: '1 1 200px' },
  btn: { border: '1px solid var(--iv-head)', background: 'var(--iv-surface)', color: 'var(--iv-head)', padding: '10px 16px', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', marginRight: 8, marginBottom: 8 },
  btnPrimary: { background: 'var(--iv-btn)', color: 'var(--iv-btn-fg)' },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  err: { color: 'var(--iv-body)', background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', padding: 12, marginBottom: 16, fontSize: '0.875rem' },
  ok: { color: 'var(--iv-success)', background: 'var(--iv-surface)', border: '1px solid var(--iv-success)', padding: 12, marginBottom: 16, fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  card: { border: '1px solid var(--iv-line)', padding: 16, marginTop: 12, background: 'var(--iv-bg)' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 },
  img: { width: '100%', border: '1px solid var(--iv-line)', display: 'block' },
  small: { fontSize: '0.78rem', color: 'var(--iv-muted)' },
  preview: { border: '1px solid var(--iv-line)', padding: 16, marginTop: 16, maxHeight: 260, overflow: 'auto', fontSize: '0.75rem', background: 'var(--iv-bg)' },
  toolbar: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 },
  toolBtn: { border: '1px solid var(--iv-line)', background: 'var(--iv-surface)', color: 'var(--iv-head)', padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer' },
  editor: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--iv-line)',
    padding: '16px 20px',
    fontSize: '0.9375rem',
    minHeight: 120,
    marginBottom: 16,
    background: 'var(--iv-raised)',
    color: 'var(--iv-body)',
    outline: 'none',
    whiteSpace: 'normal',
    wordBreak: 'normal',
    overflowWrap: 'break-word',
  },
  textareaAsInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--iv-line)',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    minHeight: 44,
    marginBottom: 16,
    background: 'var(--iv-raised)',
    color: 'var(--iv-body)',
    borderRadius: 0,
    fontFamily: 'inherit',
    lineHeight: 1.5,
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  textareaActTitle: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--iv-line)',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    minHeight: 40,
    marginBottom: 16,
    background: 'var(--iv-raised)',
    color: 'var(--iv-head)',
    borderRadius: 0,
    fontFamily: 'inherit',
    lineHeight: 1.5,
    resize: 'vertical',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  sectionTitle: {
    fontSize: '0.6875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--iv-head)',
    marginTop: 24,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid var(--iv-line)',
    fontWeight: 600,
  },
  sectionTitleFirst: { marginTop: 0 },
  readingPreviewCard: {
    border: '1px solid var(--iv-line)',
    padding: '24px 28px',
    marginTop: 12,
    background: 'var(--iv-surface)',
    maxWidth: '100%',
    overflow: 'visible',
  },
  actCaseBlock: {
    border: '1px solid var(--iv-line)',
    padding: '20px 24px',
    marginBottom: 20,
    background: 'var(--iv-surface)',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  actCaseKicker: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--iv-muted)',
    marginBottom: 8,
  },
  jsonPre: {
    border: '1px solid var(--iv-line)',
    padding: 16,
    marginTop: 8,
    maxHeight: 360,
    overflow: 'auto',
    fontSize: '0.75rem',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    lineHeight: 1.45,
    background: 'var(--iv-bg)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
};

/* Pathbuddy case HTML: airy paragraphs, <ul> quotes, <ol> with <strong>N.</strong> (no duplicate list markers). */
const STUDIO_CASE_BODY_HTML_CSS = `
.studio-case-html-body {
  font-size: 0.9375rem;
  line-height: 1.65;
  color: var(--iv-head);
  max-width: 100%;
  font-family: inherit;
}
/* One tone for body copy: lists, items, and bold option labels match paragraphs (no darker/lighter blocks). */
.studio-case-html-body p,
.studio-case-html-body ul,
.studio-case-html-body ol,
.studio-case-html-body li,
.studio-case-html-body strong,
.studio-case-html-body b,
.studio-case-html-body em,
.studio-case-html-body span {
  color: var(--iv-head);
  font-size: inherit;
  font-family: inherit;
}
.studio-case-html-body strong,
.studio-case-html-body b {
  font-weight: 600;
}
.studio-case-html-body p {
  margin: 0 0 0.9em;
}
.studio-case-html-body p:last-child {
  margin-bottom: 0;
}
.studio-case-html-body p + p {
  margin-top: 0.15em;
}
.studio-case-html-body p + ul,
.studio-case-html-body p + ol {
  margin-top: 0.35em;
}
.studio-case-html-body ul {
  margin: 0.65em 0 1em;
  padding-left: 1.85em;
  margin-left: 0.35em;
  list-style-type: disc;
}
.studio-case-html-body ul ul {
  margin: 0.35em 0 0.5em;
}
.studio-case-html-body ol {
  margin: 0.85em 0 1.1em;
  padding-left: 1.85em;
  margin-left: 0.35em;
  list-style-type: decimal;
}
.studio-case-html-body ol > li {
  margin: 0.55em 0;
  padding-left: 0.2em;
  list-style: decimal;
  list-style-type: decimal;
}
.studio-case-html-body ol ol {
  margin: 0.45em 0 0.55em;
  margin-left: 0.35em;
  padding-left: 1.65em;
}
.studio-case-html-body table {
  width: 100%;
  border-collapse: collapse;
  margin: 0.75em 0 1em;
  font-size: inherit;
}
.studio-case-html-body th,
.studio-case-html-body td {
  border: 1px solid var(--iv-line);
  padding: 8px 10px;
  text-align: left;
  vertical-align: top;
}
.studio-case-html-body th {
  font-weight: 600;
  background: var(--iv-fill);
}
.studio-case-html-body li {
  display: list-item;
}
.studio-case-html-body ul > li {
  margin: 0.4em 0;
}
.studio-case-html-body blockquote {
  margin: 0.85em 0;
  padding-left: 1.1em;
  border-left: 3px solid var(--iv-line);
  color: var(--iv-head);
}
.studio-rich-editor.studio-case-html-body {
  min-height: inherit;
}
.studio-case-reading-preview .case-preview-doc-title {
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0 0 1em;
  letter-spacing: -0.02em;
  line-height: 1.25;
}
.studio-case-reading-preview .case-preview-kicker {
  font-weight: 600;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--iv-muted);
  margin: 1.25em 0 0.4em;
}
.studio-case-reading-preview .case-preview-kicker:first-of-type {
  margin-top: 0;
}
.studio-case-reading-preview .case-preview-act-block {
  margin-top: 1.75em;
}
.studio-case-reading-preview .case-preview-act-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 0.65em;
  letter-spacing: -0.02em;
  line-height: 1.3;
  color: var(--iv-head);
}
.studio-case-reading-preview .case-preview-act-body {
  max-width: 100%;
}
/* Exhibit studio: borderless inputs inside collapsed-border grid (no “separate boxes”). */
.studio-exhibit-table-grid input:focus {
  outline: 2px solid var(--iv-btn);
  outline-offset: -2px;
}
.studio-exhibit-table-grid input::placeholder {
  color: var(--iv-muted);
}
`;

/** Case JSON contract: always original, never locked (no UI toggles). */
const applyStudioFlags = (c) => {
  if (!c || typeof c !== 'object') return c;
  return { ...c, is_original: true, is_locked: false };
};

const parseLinks = (text) => text.split(/\n|,/).map((s) => s.trim()).filter((s) => s.startsWith('http'));
const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const escapeHtmlText = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/** Plain text → Pathbuddy-style description HTML (paragraphs + <br/> like md_to_json soft breaks). */
const plainLoglineToDescriptionHtml = (text) => {
  const t = (text || '').trim();
  if (!t) return '<p></p>';
  return t
    .split(/\n\n+/)
    .filter((p) => p.trim())
    .map((p) => `<p>${escapeHtmlText(p).replace(/\n/g, '<br/>')}</p>`)
    .join('');
};

// DOMParser, not a detached div: <img onerror> fires even in a detached div.
const parseDetached = (html) => new DOMParser().parseFromString(String(html || ''), 'text/html').body;

const htmlToPlainText = (html) => {
  if (typeof document === 'undefined') {
    return stripHtml(html);
  }
  const d = parseDetached(html);
  return (d.innerText || d.textContent || '').trim();
};

/** Exhibit / logline-style fields: paragraphs → plain, joined for textarea editing. */
const htmlDescriptionToPlain = (html) => {
  if (typeof document === 'undefined') {
    return stripHtml(html);
  }
  const d = parseDetached(html);
  const ps = d.querySelectorAll('p');
  if (ps.length) {
    return [...ps].map((p) => (p.textContent || '').trim()).join('\n\n');
  }
  return (d.textContent || '').trim();
};

const stripLeadingLabelPrefix = (text, labels) => {
  const escaped = labels.map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const re = new RegExp(`^\\s*(?:${escaped})\\s*:\\s*`, 'i');
  return String(text || '').replace(re, '');
};

const stripLeadingLabelPrefixHtml = (html, labels) => {
  const raw = String(html || '');
  if (!raw) return raw;
  if (typeof document === 'undefined') {
    return stripLeadingLabelPrefix(raw, labels);
  }
  const d = parseDetached(raw);
  const walker = d.ownerDocument.createTreeWalker(d, NodeFilter.SHOW_TEXT, null);
  let node = walker.nextNode();
  while (node) {
    const current = node.nodeValue || '';
    if (current.trim().length > 0) {
      node.nodeValue = stripLeadingLabelPrefix(current, labels);
      break;
    }
    node = walker.nextNode();
  }
  return d.innerHTML;
};

const sanitizeExhibitDisplayFields = (ex) => ({
  ...ex,
  notes: stripLeadingLabelPrefixHtml(ex?.notes || '', ['Notes']),
  source: stripLeadingLabelPrefixHtml(ex?.source || '', ['Source']),
});

const sanitizeCaseExhibitDisplayFields = (caseObj) => {
  if (!caseObj || !Array.isArray(caseObj.exhibits)) return caseObj;
  return {
    ...caseObj,
    exhibits: caseObj.exhibits.map((ex) => sanitizeExhibitDisplayFields(ex || {})),
  };
};

const slugify = (s) => (s || 'case').toLowerCase().replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'case';

/** Strip tags for length checks (same idea as CaseStudy exhibit filtering). */
const stripHtmlForExhibitMeasure = (html) => String(html || '').replace(/<[^>]*>/g, '').trim();

/**
 * Auto-derived exhibit flags for JSON + reader (see CaseStudy modal: HTML_data vs image_url).
 * - is_HTML: treat the exhibit as “table / HTML_data driven” when that field has enough plain text.
 * - is_image: exhibit includes an https image, or local file(s) queued for publish enhancement.
 */
const deriveExhibitAutoFields = (ex, pendingImageFileCount) => {
  const is_HTML = stripHtmlForExhibitMeasure(ex.HTML_data).length > 10;
  const url = String(ex.image_url || '').trim();
  const is_image = /^https?:\/\//i.test(url) || pendingImageFileCount > 0;
  return { is_HTML, is_image };
};

const EXHIBIT_HTML_WRAP_ID = 'studio-exhibit-table-tools-root';

const parseExhibitHtmlWrap = (html) => {
  const raw = String(html || '');
  const doc = new DOMParser().parseFromString(`<div id="${EXHIBIT_HTML_WRAP_ID}">${raw}</div>`, 'text/html');
  const wrap = doc.getElementById(EXHIBIT_HTML_WRAP_ID);
  return { doc, wrap: wrap || doc.body };
};

const exhibitCellPlainFromEl = (el) => {
  if (!el) return '';
  return (el.textContent || '').replace(/\u00a0/g, ' ');
};

/** Parse HTML_data into plain-text header/body cells (studio form only; other markup is dropped on save). */
const parseExhibitTablesModel = (html) => {
  const { wrap } = parseExhibitHtmlWrap(html);
  const tables = [];
  wrap.querySelectorAll('table').forEach((table) => {
    const theadRow = table.querySelector('thead tr');
    const headerRow = theadRow || table.querySelector('tr');
    let headers = headerRow
      ? [...headerRow.querySelectorAll('th, td')].map((c) => exhibitCellPlainFromEl(c))
      : ['Column 1', 'Column 2'];
    const rows = [];
    const tbody = table.querySelector('tbody');
    if (tbody) {
      const tbodyTrs = [...tbody.querySelectorAll('tr')];
      // Without <thead>, querySelector('tr') is the first tbody row — do not also treat it as a data row.
      const skipFirstAsHeaderOnly =
        !theadRow && headerRow && tbodyTrs.length > 0 && tbodyTrs[0] === headerRow;
      const dataTrs = skipFirstAsHeaderOnly ? tbodyTrs.slice(1) : tbodyTrs;
      dataTrs.forEach((tr) => {
        rows.push([...tr.querySelectorAll('td, th')].map((c) => exhibitCellPlainFromEl(c)));
      });
    } else {
      table.querySelectorAll('tr').forEach((tr) => {
        if (tr.closest('thead')) return;
        if (!theadRow && headerRow && tr === headerRow) return;
        rows.push([...tr.querySelectorAll('td, th')].map((c) => exhibitCellPlainFromEl(c)));
      });
    }
    let colCount = Math.max(headers.length, 1);
    rows.forEach((r) => {
      colCount = Math.max(colCount, r.length);
    });
    while (headers.length < colCount) headers.push('');
    headers = headers.slice(0, colCount);
    const normRows =
      rows.length > 0
        ? rows.map((r) => {
            const x = [...r];
            while (x.length < colCount) x.push('');
            return x.slice(0, colCount);
          })
        : [Array.from({ length: colCount }, () => '')];
    tables.push({ headers, rows: normRows });
  });
  return { tables };
};

/**
 * Serialize tables as simple HTML only (no inline cell styles).
 * Case Study renders `HTML_data` inside `.html-content`; theme tables live in CaseStudy.css — inline styles would override that.
 */
const serializeExhibitTablesModel = (model) => {
  const tables = model?.tables || [];
  if (!tables.length) return '';
  return tables
    .map((t) => {
      const colCount = Math.max(t.headers?.length || 0, 1);
      const headers = [];
      for (let i = 0; i < colCount; i += 1) headers.push(String(t.headers[i] ?? ''));
      const thRow = headers.map((h) => `<th>${escapeHtmlText(h)}</th>`).join('');
      const bodyRows = t.rows && t.rows.length ? t.rows : [Array.from({ length: colCount }, () => '')];
      const body = bodyRows
        .map((r) => {
          const cells = [];
          for (let i = 0; i < colCount; i += 1) {
            cells.push(`<td>${escapeHtmlText(String(r[i] ?? ''))}</td>`);
          }
          return `<tr>${cells.join('')}</tr>`;
        })
        .join('');
      return `<table style="border-collapse:collapse;width:100%;margin-bottom:16px"><thead><tr>${thRow}</tr></thead><tbody>${body}</tbody></table>`;
    })
    .join('');
};

const exhibitModelAddTable = (model) => ({
  tables: [...(model.tables || []), { headers: ['Column 1', 'Column 2'], rows: [['', '']] }],
});

const exhibitModelAddRow = (model, tableIndex) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    const n = Math.max(t.headers?.length || 0, 1);
    return { ...t, rows: [...(t.rows || []), Array.from({ length: n }, () => '')] };
  }),
});

const exhibitModelAddColumn = (model, tableIndex) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    return {
      headers: [...(t.headers || []), ''],
      rows: (t.rows || []).map((r) => [...r, '']),
    };
  }),
});

const exhibitModelRemoveLastColumn = (model, tableIndex) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    if ((t.headers || []).length <= 1) return t;
    return {
      headers: t.headers.slice(0, -1),
      rows: (t.rows || []).map((r) => r.slice(0, -1)),
    };
  }),
});

const exhibitModelRemoveLastRow = (model, tableIndex) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    const rows = t.rows || [];
    if (rows.length <= 1) return t;
    return { ...t, rows: rows.slice(0, -1) };
  }),
});

const exhibitModelRemoveTable = (model, tableIndex) => ({
  tables: (model.tables || []).filter((_, i) => i !== tableIndex),
});

const exhibitModelSetHeader = (model, tableIndex, colIndex, value) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    const headers = [...(t.headers || [])];
    headers[colIndex] = value;
    return { ...t, headers };
  }),
});

const exhibitModelSetCell = (model, tableIndex, rowIndex, colIndex, value) => ({
  tables: (model.tables || []).map((t, i) => {
    if (i !== tableIndex) return t;
    const rows = (t.rows || []).map((r, ri) => {
      if (ri !== rowIndex) return r;
      const nr = [...r];
      nr[colIndex] = value;
      return nr;
    });
    return { ...t, rows };
  }),
});

/** Studio exhibit grid: collapsed borders on cells; inputs are borderless inside cells. */
const exhibitStudioTableGrid = {
  width: '100%',
  borderCollapse: 'collapse',
  border: '1px solid var(--iv-line)',
  background: 'var(--iv-raised)',
  fontSize: '0.9375rem',
  fontFamily: 'inherit',
  color: 'var(--iv-body)',
};
const exhibitStudioThCell = {
  border: '1px solid var(--iv-line)',
  padding: 0,
  margin: 0,
  background: 'var(--iv-bg)',
  fontWeight: 600,
  textAlign: 'left',
  verticalAlign: 'top',
};
const exhibitStudioTdCell = {
  border: '1px solid var(--iv-line)',
  padding: 0,
  margin: 0,
  background: 'var(--iv-surface)',
  textAlign: 'left',
  verticalAlign: 'top',
};
const exhibitStudioCellInput = {
  width: '100%',
  boxSizing: 'border-box',
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  margin: 0,
  padding: '12px 14px',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  fontWeight: 'inherit',
  background: 'transparent',
  color: 'inherit',
  textAlign: 'left',
  borderRadius: 0,
  WebkitAppearance: 'none',
  appearance: 'none',
};
const exhibitStudioHeaderCellInput = {
  ...exhibitStudioCellInput,
  fontWeight: 600,
};

const SUBJECT_OPTIONS = ['Medicine', 'Law', 'Investment Banking', 'Computer Science', 'Engineering', 'Geography', 'Management Consulting'];
const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Hard'];
const MODE_OPTIONS = ['Practice mode', 'Exam mode'];
const LNAT_MODE_OPTIONS = ['Practice by topic', 'Mock exam'];

const lnatDeliveryModeFromMode = (modeValue) => (
  String(modeValue || '').trim().toLowerCase() === 'mock exam' ? 'mock_exam' : 'practice_by_topic'
);

/**
 * Admissions, licensing, and career-stage exams mapped by Subject.
 * Values are still sent over the wire as `industry` for backend compatibility.
 */
const EXAM_SUGGESTIONS_BY_SUBJECT = {
  Medicine: [
    'UCAT / UCAT ANZ',
    'BMAT (historical / selected programmes)',
    'GAMSAT',
    'MCAT',
    'NEET-UG',
    'UKMLA / MLA',
    'USMLE Step 1',
    'USMLE Step 2 CK',
    'USMLE Step 3',
    'COMLEX Level 1–3 (DO pathway)',
    'PLAB Part 1',
    'PLAB Part 2',
    'MCCQE Part I & II (Canada)',
    'AMC MCQ & clinical (Australia)',
    'MRCS Part A',
    'MRCS Part B',
    'MRCP Part 1',
    'MRCP Part 2',
    'Prescribing Safety Assessment (PSA)',
    'Prescribing exam / licensing (jurisdiction-specific)',
    'Specialty recruitment (e.g. MSRA)',
    'Specialty board / fellowship exams',
    'Medical Council registration exams (country-specific)',
  ],
  Law: [
    'LNAT',
    'LSAT',
    'LSAT-Flex / LSAT Digital',
    'GRE (JD pathway)',
    'SQE1',
    'SQE2',
    'BCAT',
    'QLTS / SQE-equivalent conversion routes',
    'Bar Practice Course / SQE prep assessments',
    'UBE / Multistate Bar Exam (US)',
    'New York Bar Exam',
    'California Bar Exam',
    'SQE-style statutory assessments (jurisdiction-specific)',
    'CLAT (India)',
    'Cambridge Law Test',
    'Legal Practice Course / vocational finals (legacy UK)',
  ],
  'Investment Banking': [
    'GMAT',
    'GRE General',
    'CFA Level I',
    'CFA Level II',
    'CFA Level III',
    'FRM Part I',
    'FRM Part II',
    'CAIA Level I',
    'CAIA Level II',
    'Series SIE',
    'Series 7',
    'Series 63',
    'Series 66',
    'Series 79',
    'ACA / ACCA pathway exams',
    'FMVA / financial modeling certifications',
    'Investment banking / markets graduate schemes — aptitude tests',
  ],
  'Computer Science': [
    'TMUA',
    'STEP',
    'TARA (Test of Academic Reasoning for Admissions)',
    'MAT (Oxford)',
    'TSA Oxford',
    'PAT (Oxford)',
    'AP Computer Science A',
    'AP Computer Science Principles',
    'IB Computer Science HL',
    'GRE General (graduate school)',
    'AWS Certified Solutions Architect — Associate',
    'AWS Certified Developer — Associate',
    'Google Professional Cloud Developer',
    'Azure Developer Associate (AZ-204)',
    'Certified Kubernetes Administrator (CKA)',
    'Certified Kubernetes Application Developer (CKAD)',
    '(ISC)² Certified Information Systems Security Professional (CISSP)',
    'Offensive Security OSCP',
  ],
  Engineering: [
    'ENGAA (Cambridge Engineering)',
    'PAT (Oxford Physics / Engineering)',
    'Physics Admissions Test variants (institution-specific)',
    'FE Fundamentals of Engineering (US EIT)',
    'FE discipline exams (civil / mechanical / electrical etc.)',
    'Principles & Practice of Engineering (PE exam)',
    'Chartered Engineer (CEng) competence assessment (UK)',
    'EUR ING / FEANI examinations',
    'Six Sigma Green Belt',
    'Six Sigma Black Belt',
    'Project Management Professional (PMP)',
    'ASE certification (automotive)',
    'PE licensure continuing competency / state exams',
  ],
  Geography: [
    'AP Human Geography',
    'AP Environmental Science',
    'IB Geography HL',
    'A-Level Geography / equivalent national finals',
    'GRE General (graduate Geography / GIS programmes)',
    'GIS Professional (GISP)',
    'Esri Technical Certification',
    'Chartered Geographer (CGeog)',
    'Royal Geographical Society professional accreditation pathway',
    'Remote sensing / Earth observation vendor certifications',
    'Environmental impact assessment practitioner exams / licences',
    'Planning / chartered town planner assessments (jurisdiction-specific)',
  ],
  'Management Consulting': [
    'GMAT',
    'GRE General',
    'McKinsey Solve / Problem Solving Game',
    'McKinsey PST-style assessments (legacy)',
    'BCG Online Case / Potential Test',
    'Bain SOVA / aptitude tests',
    'Oliver Wyman / numerical reasoning screens',
    'Kearney / L.E.K. style quantitative tests',
    'MBA admissions standardized tests (school-specific)',
    'Certified Management Consultant (CMC)',
    'PMI Agile / Scrum certifications (delivery consulting)',
  ],
};

/** Same three outputs as backend publish / photo_to_resize (order: small, medium, large). */
const CASE_PUBLISH_SIZES = [
  [140, 196],
  [260, 146],
  [1920, 1080],
];

/**
 * Centre crop-to-fill to exact WxH (same math as server PIL pipeline).
 * Returns PNG data URL for preview only; server still does final encode.
 */
const canvasCropToDataUrl = (img, targetW, targetH) => {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih) return '';
  const scale = Math.max(targetW / iw, targetH / ih);
  const cropW = targetW / scale;
  const cropH = targetH / scale;
  const sx = (iw - cropW) / 2;
  const sy = (ih - cropH) / 2;
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, targetW, targetH);
  return canvas.toDataURL('image/png');
};

const RichEditor = ({ label, value, onChange, minHeight = 120 }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const current = ref.current.innerHTML;
    if (current === (value || '')) return;
    const safe = sanitizeHtml(value || '');
    if (current !== safe) ref.current.innerHTML = safe;
  }, [value]);

  const apply = (cmd, cmdValue = null) => {
    if (!ref.current) return;
    ref.current.focus();
    document.execCommand(cmd, false, cmdValue);
    onChange(ref.current.innerHTML);
  };

  const flush = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  return (
    <div>
      <label style={styles.label}>{label}</label>
      <div style={styles.toolbar}>
        <button type="button" style={styles.toolBtn} onClick={() => apply('bold')}><b>B</b></button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('italic')}><i>I</i></button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('insertUnorderedList')}>• List</button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('insertOrderedList')}>1. List</button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('indent')}>Indent</button>
        <button type="button" style={styles.toolBtn} onClick={() => apply('outdent')}>Outdent</button>
      </div>
      <div
        className="studio-rich-editor studio-case-html-body"
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current ? ref.current.innerHTML : '')}
        onBlur={flush}
        style={{ ...styles.editor, minHeight }}
      />
    </div>
  );
};

/** Full-document reading preview: logline + each act as one block (title + HTML body — options live inside act HTML). */
const CaseReadingPreview = ({ caseJson }) => {
  if (!caseJson) return null;
  const act = (n) => caseJson[`act_${n}`] || {};
  return (
    <div className="studio-case-reading-preview" style={styles.readingPreviewCard}>
      <div className="case-preview-doc-title">{caseJson.title || 'Untitled'}</div>
      <div className="case-preview-kicker">Logline</div>
      <div
        className="studio-case-html-body"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(caseJson.description) || '<p></p>' }}
      />
      {[1, 2, 3].map((n) => {
        const a = act(n);
        const t = (a.title || '').trim() || `Act ${n}`;
        return (
          <section key={n} className="case-preview-act-block">
            <div className="case-preview-kicker">{`Act ${n}`}</div>
            <div className="case-preview-act-title">{t}</div>
            <div
              className="studio-case-html-body case-preview-act-body"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.content) || '<p></p>' }}
            />
          </section>
        );
      })}
    </div>
  );
};

const LNAT_RUN_ALL_STEPS = [
  { key: 'step1', label: 'Step 1 — Generated passage' },
  { key: 'step2', label: 'Step 2 — Passage validation' },
  { key: 'step3', label: 'Step 3 — Questions' },
  { key: 'step4', label: 'Step 4 — Question validation' },
  { key: 'step5', label: 'Step 5 — Compiled explanations' },
  { key: 'step6', label: 'Step 6 — Database JSON' },
  { key: 'step7', label: 'Step 7 — Verification' },
  { key: 'step8', label: 'Step 8 — Examination' },
];

function lnatBundleHasAnyStep(bundle) {
  if (!bundle || typeof bundle !== 'object') return false;
  return LNAT_RUN_ALL_STEPS.some(({ key }) => {
    const data = bundle[key];
    return data && typeof data === 'object' && Object.keys(data).length > 0;
  });
}

function buildLnatCurrentPassageBundle(state) {
  const bundle = {};
  if (state.passage) bundle.step1 = state.passage;
  if (state.passageValidation) bundle.step2 = state.passageValidation;
  if (state.questions) bundle.step3 = state.questions;
  if (state.questionValidation) bundle.step4 = state.questionValidation;
  if (state.compiled) bundle.step5 = state.compiled;
  if (state.database) bundle.step6 = state.database;
  if (state.verification) bundle.step7 = state.verification;
  if (state.examination) bundle.step8 = state.examination;
  return lnatBundleHasAnyStep(bundle) ? bundle : null;
}

function buildLnatPassageSyncMap(allResults, currentPassageNum, currentBundle) {
  const out = {};
  Object.entries(allResults || {}).forEach(([num, bundle]) => {
    if (lnatBundleHasAnyStep(bundle)) out[String(num)] = bundle;
  });
  if (currentBundle && lnatBundleHasAnyStep(currentBundle)) {
    const key = String(currentPassageNum);
    out[key] = { ...(out[key] || {}), ...currentBundle };
  }
  return out;
}

const LNAT_STUDIO_PROGRESS_KEY = 'pathbuddy_lnat_exam_studio_progress';

function readLnatStudioProgress() {
  try {
    const raw = sessionStorage.getItem(LNAT_STUDIO_PROGRESS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLnatStudioProgress(payload) {
  try {
    sessionStorage.setItem(LNAT_STUDIO_PROGRESS_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

function lnatPassageBundleComplete(bundle) {
  const step8 = bundle?.step8;
  return Boolean(step8 && typeof step8 === 'object' && Object.keys(step8).length > 0);
}

function lnatCompletedPassageNumbers(results, passageTotal) {
  const done = [];
  for (let p = 1; p <= passageTotal; p += 1) {
    if (lnatPassageBundleComplete(results?.[p])) done.push(p);
  }
  return done;
}

function lnatFirstIncompletePassage(results, passageTotal) {
  for (let p = 1; p <= passageTotal; p += 1) {
    if (!lnatPassageBundleComplete(results?.[p])) return p;
  }
  return null;
}

const LNAT_ACTIVE_STEP_LABEL = {
  step1_generate_passage: 'Step 1',
  step2_validate_passage: 'Step 2',
  step3_generate_questions: 'Step 3',
  step4_validate_questions: 'Step 4',
  step5_compile_explanations: 'Step 5',
  step6_compile_database: 'Step 6',
  step7_verify_questions: 'Step 7',
  step8_examine_questions: 'Step 8',
};

function lnatPassageStatusLine(passageNum, passageTotal, { runAllProgress, activeStep, busy }) {
  const base = `Passage ${passageNum} of ${passageTotal}`;
  if (runAllProgress) {
    return `${base} · ${runAllProgress}`;
  }
  if (busy && activeStep) {
    const stepLabel = LNAT_ACTIVE_STEP_LABEL[activeStep];
    if (stepLabel) {
      return `${base} · Passage ${passageNum} — ${stepLabel}`;
    }
  }
  return base;
}

function mergeLnatPassageBundle(clientBundle, serverBundle) {
  if (!clientBundle && !serverBundle) return null;
  if (!clientBundle) return serverBundle;
  if (!serverBundle) return clientBundle;
  const out = { ...serverBundle };
  LNAT_RUN_ALL_STEPS.forEach(({ key }) => {
    const c = clientBundle[key];
    const s = serverBundle[key];
    if (c && s) {
      const cKeys = typeof c === 'object' ? Object.keys(c).length : 0;
      const sKeys = typeof s === 'object' ? Object.keys(s).length : 0;
      out[key] = cKeys >= sKeys ? c : s;
    } else if (c) {
      out[key] = c;
    } else if (s) {
      out[key] = s;
    }
  });
  return out;
}

function mergeLnatAllPassageResults(clientResults, serverPassages) {
  const merged = { ...(clientResults || {}) };
  if (!serverPassages || typeof serverPassages !== 'object') return merged;
  Object.entries(serverPassages).forEach(([rawKey, serverBundle]) => {
    const num = Number(rawKey);
    if (!Number.isFinite(num) || num < 1) return;
    merged[num] = mergeLnatPassageBundle(merged[num], serverBundle);
  });
  return merged;
}

function lnatIsRecoverableBuildError(message) {
  const msg = String(message || '');
  return /timed out|timeout|401|403|authentication|unauthorized|failed to fetch|network|err_name_not_resolved|name_not_resolved|abort/i.test(
    msg,
  );
}

function lnatBuildStoppedUserMessage(err) {
  const msg = String(err?.message || err || 'Build stopped');
  const resumeHint =
    ' Progress is kept in this browser session. Use “Save partial progress to ExamBuilderJson” if needed, then click “Build full exam” again to resume from the first incomplete passage (completed passages are skipped).';
  if (lnatIsRecoverableBuildError(msg)) {
    return `${msg}${resumeHint}`;
  }
  return `${msg}${resumeHint}`;
}

function lnatQuestionsFromPayload(data) {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.questions)) return data.questions;
  if (Array.isArray(data.subsection?.questions)) return data.subsection.questions;
  if (Array.isArray(data.compiled_subsection?.questions)) return data.compiled_subsection.questions;
  return [];
}

function lnatIsTwoPassageStep1(data) {
  if (!data || typeof data !== 'object') return false;
  return Boolean(
    data.passage_A?.passage_object?.text
    || data.passage_B?.passage_object?.text
    || data.archetype === 'TWO_PASSAGE'
    || data.schema_name?.includes?.('TWO_PASSAGE')
  );
}

function lnatStep1PassageTitle(data) {
  if (!data || typeof data !== 'object') return '';
  if (lnatIsTwoPassageStep1(data)) {
    return (
      data.pair_object?.examiner_neutral_title
      || data.passage_A?.passage_object?.title
      || data.passage_B?.passage_object?.title
      || ''
    );
  }
  return data.passage_object?.title || data.passage_object?.topic_tag || '';
}

function lnatCountWords(text) {
  const t = String(text ?? '').trim();
  if (!t) return 0;
  return (t.match(/\b[\w'-]+\b/g) || []).length;
}

function lnatStep1WordCount(data) {
  if (!data || typeof data !== 'object') return 0;
  if (lnatIsTwoPassageStep1(data)) {
    const a = lnatCountWords(lnatPassageObjectPlainText(data.passage_A?.passage_object));
    const b = lnatCountWords(lnatPassageObjectPlainText(data.passage_B?.passage_object));
    return a + b || 0;
  }
  const po = data.passage_object;
  const fromText = lnatCountWords(lnatPassageObjectPlainText(po));
  const reported = Number(po?.word_count) || 0;
  return fromText || reported;
}

/** Plain passage body for UI: prefer `text`, but rebuild from `paragraphs` when breaks are missing. */
function lnatPassageObjectPlainText(po) {
  if (!po || typeof po !== 'object') return '';
  const paragraphs = Array.isArray(po.paragraphs) ? po.paragraphs : [];
  const fromParagraphs = paragraphs
    .map((p) => (typeof p === 'string' ? p : String(p?.text ?? '')).trim())
    .filter(Boolean)
    .join('\n\n');
  const text = String(po.text ?? '').trim();
  if (fromParagraphs && (!text || !/\n\s*\n/.test(text))) {
    return fromParagraphs;
  }
  return text || fromParagraphs;
}

function lnatApplyPassageObjectText(po, nextText) {
  const base = po && typeof po === 'object' ? po : {};
  const nextParagraphs = nextText
    .split(/\n\s*\n/g)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((text, idx) => ({ pid: `p${idx + 1}`, text }));
  const nextWordCount = nextText.trim().split(/\s+/).filter(Boolean).length;
  return {
    ...base,
    text: nextText,
    paragraphs: nextParagraphs,
    word_count: nextWordCount,
  };
}

/** Rich HTML for passage styling editor (preserves existing tags in paragraph text). */
function lnatPassageObjectToRichHtml(po) {
  if (!po || typeof po !== 'object') return '<p></p>';
  const paragraphs = Array.isArray(po.paragraphs) ? po.paragraphs : [];
  const htmlParts = paragraphs
    .map((p) => {
      const t = (typeof p === 'string' ? p : String(p?.text ?? '')).trim();
      if (!t) return '';
      return t.startsWith('<') ? t : `<p>${t.replace(/\n/g, '<br>')}</p>`;
    })
    .filter(Boolean);
  if (htmlParts.length) return htmlParts.join('');
  return lnatTextToRichHtml(lnatPassageObjectPlainText(po));
}

function lnatApplyPassageObjectHtml(po, html) {
  const base = po && typeof po === 'object' ? po : {};
  const raw = String(html ?? '').trim() || '<p></p>';
  const parts = [];
  const re = /<p[^>]*>([\s\S]*?)<\/p>/gi;
  let match = re.exec(raw);
  if (match) {
    do {
      const inner = match[1].trim();
      if (inner) parts.push(inner);
      match = re.exec(raw);
    } while (match);
  }
  if (!parts.length) {
    const stripped = raw.replace(/^<p[^>]*>|<\/p>$/gi, '').trim();
    if (stripped) parts.push(stripped);
  }
  const paragraphs = parts.map((text, idx) => ({ pid: `p${idx + 1}`, text }));
  const plainJoined = parts.map((t) => t.replace(/<[^>]+>/g, ' ')).join(' ');
  const textField = parts.join('\n\n');
  return {
    ...base,
    text: textField,
    paragraphs,
    word_count: lnatCountWords(plainJoined),
  };
}

function LnatPassageBodyPreview({ passageObject, emptyLabel = '(No passage text)' }) {
  const paragraphs = Array.isArray(passageObject?.paragraphs) ? passageObject.paragraphs : [];
  const hasHtml = paragraphs.some((p) => String(typeof p === 'string' ? p : p?.text ?? '').includes('<'))
    || String(passageObject?.text ?? '').includes('<');
  if (hasHtml) {
    const html = paragraphs.length
      ? paragraphs
        .map((p) => {
          const t = (typeof p === 'string' ? p : String(p?.text ?? '')).trim();
          if (!t) return '';
          return t.startsWith('<') ? t : `<p>${t}</p>`;
        })
        .filter(Boolean)
        .join('')
      : lnatPassageObjectToRichHtml(passageObject);
    if (!html.trim()) {
      return <div style={{ ...styles.small, color: 'var(--iv-muted)', marginTop: 6 }}>{emptyLabel}</div>;
    }
    return (
      <div
        className="studio-case-html-body"
        style={{
          ...styles.readingPreviewCard,
          marginTop: 0,
          marginBottom: 8,
          maxHeight: 480,
          overflow: 'auto',
        }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
      />
    );
  }
  const plain = lnatPassageObjectPlainText(passageObject);
  if (!plain.trim()) {
    return <div style={{ ...styles.small, color: 'var(--iv-muted)', marginTop: 6 }}>{emptyLabel}</div>;
  }
  return (
    <div
      className="studio-case-html-body"
      style={{
        ...styles.readingPreviewCard,
        marginTop: 0,
        marginBottom: 8,
        maxHeight: 480,
        overflow: 'auto',
      }}
      dangerouslySetInnerHTML={{ __html: plainLoglineToDescriptionHtml(plain) }}
    />
  );
}

function LnatPassagePartStylePanel({ passageObject, label, onApplyHtml }) {
  const [mode, setMode] = useState('preview');
  return (
    <div>
      {label ? <div style={{ ...styles.small, marginBottom: 4, fontWeight: 600 }}>{label}</div> : null}
      <LnatPreviewEditToggle
        mode={mode}
        onPreview={() => setMode('preview')}
        onEdit={() => setMode('edit')}
      />
      {mode === 'preview' ? (
        <LnatPassageBodyPreview passageObject={passageObject} />
      ) : (
        <RichEditor
          label="Passage text (styling only)"
          value={lnatPassageObjectToRichHtml(passageObject)}
          onChange={onApplyHtml}
          minHeight={200}
        />
      )}
    </div>
  );
}

function lnatMutateQuestionsInPayload(payload, questionId, mutator) {
  if (!payload || typeof payload !== 'object') return payload;
  const next = JSON.parse(JSON.stringify(payload));
  const lists = [];
  if (Array.isArray(next.questions)) lists.push(next.questions);
  if (Array.isArray(next.subsection?.questions)) lists.push(next.subsection.questions);
  if (Array.isArray(next.compiled_subsection?.questions)) lists.push(next.compiled_subsection.questions);
  for (const arr of lists) {
    const idx = arr.findIndex((q) => q && q.question_id === questionId);
    if (idx >= 0) {
      arr[idx] = mutator(arr[idx]);
      return next;
    }
  }
  return next;
}

function lnatTextToRichHtml(text) {
  const t = String(text ?? '').trim();
  if (!t) return '<p></p>';
  if (t.startsWith('<')) return t;
  const escaped = t
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped.replace(/\n\n+/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
}

function lnatWrapStemAsQuestionHtml(stem) {
  const text = String(stem ?? '').trim();
  if (!text) return '<p></p>';
  if (text.startsWith('<')) return text;
  return `<p>${text}</p>`;
}

function lnatMutateDatabaseRecord(payload, externalQuestionId, mutator) {
  if (!payload || typeof payload !== 'object' || !Array.isArray(payload.questions)) return payload;
  const next = JSON.parse(JSON.stringify(payload));
  const idx = next.questions.findIndex((r) => r && r.external_question_id === externalQuestionId);
  if (idx >= 0) next.questions[idx] = mutator(next.questions[idx]);
  return next;
}

function lnatExternalIdFromQuestionId(questionId, passageNumber = 1) {
  const m = String(questionId || '').match(/Q(\d+)/i);
  const slot = m ? m[1] : '1';
  return `LNAT-0001-p${passageNumber}-q${slot}`;
}

function LnatPassageFields({ passage, locked, onChange }) {
  if (!passage) return null;

  const renderPart = (partKey, part, label) => {
    const po = part?.passage_object || {};
    const title = (po.title || '').trim();
    const displayText = lnatPassageObjectPlainText(po);
    return (
      <div key={partKey} style={{ marginTop: partKey === 'B' ? 14 : 0 }}>
        <label style={styles.label}>
          {label}
          {title ? ` — ${title}` : ''}
        </label>
        {locked ? (
          <LnatPassagePartStylePanel
            passageObject={po}
            onApplyHtml={(html) => {
              onChange((prev) => {
                const p = prev || {};
                if (partKey === 'A') {
                  return {
                    ...p,
                    passage_A: {
                      ...(p.passage_A || {}),
                      passage_object: lnatApplyPassageObjectHtml(p.passage_A?.passage_object, html),
                    },
                  };
                }
                return {
                  ...p,
                  passage_B: {
                    ...(p.passage_B || {}),
                    passage_object: lnatApplyPassageObjectHtml(p.passage_B?.passage_object, html),
                  },
                };
              });
            }}
          />
        ) : (
          <textarea
            style={{ ...styles.textarea, minHeight: 200, marginBottom: 8 }}
            value={displayText}
            onChange={(e) => {
              const nextText = e.target.value;
              onChange((prev) => {
                const p = prev || {};
                if (partKey === 'A') {
                  return {
                    ...p,
                    passage_A: {
                      ...(p.passage_A || {}),
                      passage_object: lnatApplyPassageObjectText(p.passage_A?.passage_object, nextText),
                    },
                  };
                }
                return {
                  ...p,
                  passage_B: {
                    ...(p.passage_B || {}),
                    passage_object: lnatApplyPassageObjectText(p.passage_B?.passage_object, nextText),
                  },
                };
              });
            }}
          />
        )}
      </div>
    );
  };

  if (lnatIsTwoPassageStep1(passage)) {
    const pairTitle = (passage.pair_object?.examiner_neutral_title || '').trim();
    return (
      <>
        {pairTitle ? (
          <div style={{ ...styles.small, marginBottom: 8 }}>
            <strong>Pair title:</strong> {pairTitle}
          </div>
        ) : null}
        {renderPart('A', passage.passage_A, 'Passage A')}
        {renderPart('B', passage.passage_B, 'Passage B')}
      </>
    );
  }

  const po = passage.passage_object || {};
  const displayText = lnatPassageObjectPlainText(po);

  if (locked) {
    return (
      <LnatPassagePartStylePanel
        passageObject={po}
        onApplyHtml={(html) => {
          onChange((prev) => ({
            ...(prev || {}),
            passage_object: lnatApplyPassageObjectHtml(prev?.passage_object, html),
          }));
        }}
      />
    );
  }

  return (
    <textarea
      style={{ ...styles.textarea, minHeight: 220, flex: 1, margin: 0 }}
      value={displayText}
      onChange={(e) => {
        const nextText = e.target.value;
        onChange((prev) => ({
          ...(prev || {}),
          passage_object: lnatApplyPassageObjectText(prev?.passage_object, nextText),
        }));
      }}
    />
  );
}

function LnatFieldPreview({ value, emptyLabel = '(empty)' }) {
  const t = String(value ?? '').trim();
  if (!t) {
    return <div style={{ marginTop: 6, color: 'var(--iv-muted)' }}>{emptyLabel}</div>;
  }
  if (t.startsWith('<')) {
    return (
      <div
        className="studio-case-html-body"
        style={{ marginTop: 6 }}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(t) }}
      />
    );
  }
  return (
    <div
      className="studio-case-html-body"
      style={{ marginTop: 6 }}
      dangerouslySetInnerHTML={{ __html: plainLoglineToDescriptionHtml(t) }}
    />
  );
}

function LnatPreviewEditToggle({ mode, onPreview, onEdit }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 4, flexWrap: 'wrap' }}>
      <button
        type="button"
        style={{ ...styles.btn, marginBottom: 0, ...(mode === 'preview' ? styles.btnPrimary : {}) }}
        onClick={onPreview}
      >
        Preview
      </button>
      <button
        type="button"
        style={{ ...styles.btn, marginBottom: 0, ...(mode === 'edit' ? styles.btnPrimary : {}) }}
        onClick={onEdit}
      >
        Edit styling
      </button>
    </div>
  );
}

function LnatQuestionStyleEditors({
  q,
  idx,
  onStemChange,
  onOptionChange,
  onExplanationChange,
  showExplanation = false,
}) {
  const qid = q.question_id || `Q${idx + 1}`;
  return (
    <>
      <RichEditor
        label="Question stem (styling only)"
        value={lnatTextToRichHtml(q.stem)}
        onChange={onStemChange}
        minHeight={72}
      />
      {Array.isArray(q.options) ? (
        <div style={{ marginTop: 8 }}>
          {q.options.map((opt, optIdx) => {
            const label = opt.label || String.fromCharCode(65 + optIdx);
            return (
              <div key={`${qid}-opt-${label}`} style={{ marginTop: 8 }}>
                <RichEditor
                  label={`Option ${label} (styling only)`}
                  value={lnatTextToRichHtml(opt.text)}
                  onChange={(html) => onOptionChange(label, html)}
                  minHeight={56}
                />
              </div>
            );
          })}
        </div>
      ) : null}
      {showExplanation && (q.explanation_html || q.explanation) ? (
        <div style={{ marginTop: 8 }}>
          <RichEditor
            label="Explanation (styling only)"
            value={lnatTextToRichHtml(q.explanation_html || q.explanation)}
            onChange={onExplanationChange}
            minHeight={100}
          />
        </div>
      ) : null}
    </>
  );
}

function LnatQuestionStylePanel({
  q,
  idx,
  onStemChange,
  onOptionChange,
  onExplanationChange,
  showExplanation = false,
}) {
  const [mode, setMode] = useState('preview');
  const qid = q.question_id || `Q${idx + 1}`;

  return (
    <div>
      <LnatPreviewEditToggle
        mode={mode}
        onPreview={() => setMode('preview')}
        onEdit={() => setMode('edit')}
      />
      {mode === 'preview' ? (
        <>
          <LnatFieldPreview value={q.stem} emptyLabel="(No stem returned)" />
          {Array.isArray(q.options) ? (
            <div style={{ marginTop: 8 }}>
              {q.options.map((opt, optIdx) => {
                const label = opt.label || String.fromCharCode(65 + optIdx);
                const text = opt.text || '';
                const isHtml = String(text).trim().startsWith('<');
                return (
                  <div key={`${qid}-prev-${label}`} style={styles.small}>
                    <strong>{label}.</strong>
                    {isHtml ? (
                      <div
                        className="studio-case-html-body"
                        style={{ marginTop: 4, marginLeft: 12 }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}
                      />
                    ) : (
                      <span> {text}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
          {showExplanation && (q.explanation_html || q.explanation) ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ ...styles.small, marginBottom: 4 }}>Explanation</div>
              <LnatFieldPreview value={q.explanation_html || q.explanation} />
            </div>
          ) : null}
        </>
      ) : (
        <LnatQuestionStyleEditors
          q={q}
          idx={idx}
          showExplanation={showExplanation}
          onStemChange={onStemChange}
          onOptionChange={onOptionChange}
          onExplanationChange={onExplanationChange}
        />
      )}
    </div>
  );
}

function LnatDbRecordStylePanel({ rec, onQuestionTextChange, onExplanationChange }) {
  const [mode, setMode] = useState('preview');

  return (
    <div>
      <LnatPreviewEditToggle
        mode={mode}
        onPreview={() => setMode('preview')}
        onEdit={() => setMode('edit')}
      />
      {mode === 'preview' ? (
        <>
          <LnatFieldPreview value={rec.question_text} emptyLabel="(No question text)" />
          {rec.explanation ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ ...styles.small, marginBottom: 4 }}>Explanation</div>
              <LnatFieldPreview value={rec.explanation} />
            </div>
          ) : null}
        </>
      ) : (
        <>
          <RichEditor
            label="Question text (styling only)"
            value={rec.question_text || '<p></p>'}
            onChange={onQuestionTextChange}
            minHeight={72}
          />
          {rec.explanation ? (
            <div style={{ marginTop: 8 }}>
              <RichEditor
                label="Explanation (styling only)"
                value={rec.explanation || '<p></p>'}
                onChange={onExplanationChange}
                minHeight={100}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function lnatRunAllStepSummary(stepKey, data) {
  if (!data || typeof data !== 'object') return 'no data';
  switch (stepKey) {
    case 'step1': {
      const t = lnatStep1PassageTitle(data);
      const wc = lnatStep1WordCount(data);
      const two = lnatIsTwoPassageStep1(data) ? '2 passages' : '';
      return [t, wc ? `${wc} words` : '', two].filter(Boolean).join(' · ') || 'passage';
    }
    case 'step2':
      return String(data?.decision || data?.validation_decision || '—');
    case 'step3':
      return `${lnatQuestionsFromPayload(data).length} questions`;
    case 'step4': {
      const d = String(data?.validation_decision || data?.decision || '').toUpperCase();
      return d || '—';
    }
    case 'step5': {
      const sub = data?.compiled_subsection || data?.subsection;
      const n = Array.isArray(sub?.questions) ? sub.questions.length : lnatQuestionsFromPayload(data).length;
      return `${n} compiled`;
    }
    case 'step6':
      return `${Array.isArray(data?.questions) ? data.questions.length : 0} DB records`;
    case 'step7':
    case 'step8': {
      const reps = Array.isArray(data?.questions) ? data.questions : [];
      const verdicts = reps.map((r) => r.final_verdict).filter(Boolean);
      return verdicts.length ? verdicts.join(', ') : `${reps.length} reports`;
    }
    default:
      return 'output';
  }
}

function LnatRunAllStepBody({ stepKey, data }) {
  if (!data) {
    return <div style={styles.small}>No output for this step.</div>;
  }
  if (stepKey === 'step1') {
    if (lnatIsTwoPassageStep1(data)) {
      return (
        <>
          <LnatPassageFields passage={data} locked onChange={() => {}} />
          <pre style={{ ...styles.preview, maxHeight: 320, marginTop: 8 }}>{JSON.stringify(data, null, 2)}</pre>
        </>
      );
    }
    const po = data?.passage_object;
    return (
      <>
        {po ? <LnatPassageBodyPreview passageObject={po} /> : null}
        <pre style={{ ...styles.preview, maxHeight: 320 }}>{JSON.stringify(data, null, 2)}</pre>
      </>
    );
  }
  if (stepKey === 'step3' || stepKey === 'step5') {
    const qs = lnatQuestionsFromPayload(data);
    const sub = stepKey === 'step5' ? (data?.compiled_subsection || data?.subsection) : null;
    const list = stepKey === 'step5' && sub?.questions ? sub.questions : qs;
    return (
      <>
        {list.map((q, idx) => (
          <div key={q.question_id || `${stepKey}-${idx}`} style={{ ...styles.card, marginTop: 8 }}>
            <div style={styles.small}><strong>{q.question_id || `Q${idx + 1}`}</strong></div>
            <div style={{ marginTop: 6 }}>{q.stem || ''}</div>
            {(q.explanation_html || q.explanation) ? (
              <StudioMathHtml
                html={q.explanation_html || q.explanation}
                style={{ marginTop: 8, fontSize: '0.875rem' }}
              />
            ) : null}
          </div>
        ))}
        <pre style={{ ...styles.preview, maxHeight: 280, marginTop: 8 }}>{JSON.stringify(data, null, 2)}</pre>
      </>
    );
  }
  if (stepKey === 'step6' && Array.isArray(data?.questions)) {
    return (
      <>
        {data.questions.map((rec, idx) => (
          <div key={rec.external_question_id || `db-${idx}`} style={{ ...styles.card, marginTop: 8 }}>
            <div style={styles.small}><strong>{rec.external_question_id || `Record ${idx + 1}`}</strong></div>
          </div>
        ))}
        <pre style={{ ...styles.preview, maxHeight: 280, marginTop: 8 }}>{JSON.stringify(data, null, 2)}</pre>
      </>
    );
  }
  if ((stepKey === 'step7' || stepKey === 'step8') && Array.isArray(data?.questions)) {
    return (
      <>
        {data.questions.map((rep, idx) => (
          <div key={rep.external_question_id || `${stepKey}-${idx}`} style={{ ...styles.card, marginTop: 8 }}>
            <div style={styles.small}>
              <strong>{rep.external_question_id || `Q${idx + 1}`}</strong>
              {rep.final_verdict ? ` — ${rep.final_verdict}` : ''}
            </div>
            {rep.report_markdown ? (
              <pre style={{ ...styles.preview, maxHeight: 280, whiteSpace: 'pre-wrap', marginTop: 8 }}>
                {rep.report_markdown}
              </pre>
            ) : null}
          </div>
        ))}
        <pre style={{ ...styles.preview, maxHeight: 220, marginTop: 8 }}>{JSON.stringify(data, null, 2)}</pre>
      </>
    );
  }
  return <pre style={{ ...styles.preview, maxHeight: 360 }}>{JSON.stringify(data, null, 2)}</pre>;
}

function LnatRunAllOutputsPanel({
  results,
  fullExam,
  onOpenPassage,
  onSavePartial,
  savingPartial,
  savePartialMessage,
}) {
  const passageNums = Object.keys(results || {})
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  if (!passageNums.length) return null;

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--iv-line)' }}>
      <div style={{ ...styles.label, textTransform: 'none', letterSpacing: 0, fontSize: '0.875rem' }}>
        Full exam outputs — open each passage, then each step
      </div>
      {onSavePartial ? (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            style={{ ...styles.btn, ...(savingPartial ? styles.btnDisabled : {}) }}
            disabled={savingPartial}
            onClick={onSavePartial}
            title="Upload completed passages (and any in-progress steps) to ExamBuilderJson, even if the full build timed out"
          >
            {savingPartial ? 'Saving progress…' : 'save partial progress to repo'}
          </button>
          {savePartialMessage ? (
            <div style={{ marginTop: 8, fontSize: '0.875rem', color: 'var(--iv-success)' }}>{savePartialMessage}</div>
          ) : null}
        </div>
      ) : null}
      {fullExam ? (
        <details style={{ ...styles.card, marginTop: 10 }} open>
          <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0, textTransform: 'none', letterSpacing: 0 }}>
            Combined exam JSON
            {' '}
            <span style={{ fontWeight: 400, color: 'var(--iv-muted)' }}>
              {fullExam.question_count} questions · {fullExam.passage_count} passages
            </span>
          </summary>
          <div style={{ ...styles.small, marginTop: 8 }}>
            File: {fullExam.output_file || 'full_exam_LNAT-0001-paper2.json'}
          </div>
          <pre style={{ ...styles.preview, maxHeight: 360, marginTop: 8 }}>
            {JSON.stringify(fullExam, null, 2)}
          </pre>
        </details>
      ) : null}
      <div style={styles.small}>Completed passages: {passageNums.join(', ')}</div>
      {passageNums.map((num) => {
        const bundle = results[num];
        const title = lnatStep1PassageTitle(bundle?.step1);
        return (
          <details key={`run-all-p-${num}`} style={{ ...styles.card, marginTop: 10 }}>
            <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0, textTransform: 'none', letterSpacing: 0 }}>
              Passage {num}
              {title ? ` — ${title}` : ''}
              {' '}
              <span style={{ fontWeight: 400, color: 'var(--iv-muted)' }}>(8 steps)</span>
            </summary>
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                style={{ ...styles.btn, marginBottom: 8 }}
                onClick={() => onOpenPassage(num, bundle)}
              >
                Load passage {num} in panel below
              </button>
              {LNAT_RUN_ALL_STEPS.map(({ key, label }) => (
                <details key={`${num}-${key}`} style={{ marginTop: 6, marginLeft: 4 }}>
                  <summary style={{ ...styles.small, cursor: 'pointer', fontWeight: 600 }}>
                    {label}
                    {' — '}
                    {lnatRunAllStepSummary(key, bundle?.[key])}
                  </summary>
                  <div style={{ marginTop: 8, marginLeft: 8 }}>
                    <LnatRunAllStepBody stepKey={key} data={bundle?.[key]} />
                  </div>
                </details>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
}

const ExamStudioPage = () => {
  const [subject, setSubject] = useState('Engineering');
  const [industry, setIndustry] = useState('General');
  const [difficulty, setDifficulty] = useState('Medium');
  const [mode, setMode] = useState('Practice mode');
  const [creatorName, setCreatorName] = useState('');
  const [lnatMockExamNumber, setLnatMockExamNumber] = useState(1);
  const [logline, setLogline] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [domainNotes, setDomainNotes] = useState('');
  const [linksText, setLinksText] = useState('');
  const [files, setFiles] = useState([]);

  const [caseJson, setCaseJson] = useState(null);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [coverItems, setCoverItems] = useState([]);
  const [selectedCoverId, setSelectedCoverId] = useState('');
  const [promptOverride, setPromptOverride] = useState('');
  const [error, setError] = useState('');
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [loadingFinalLoglines, setLoadingFinalLoglines] = useState(false);
  const [finalLoglineSuggestions, setFinalLoglineSuggestions] = useState([]);
  const [loadingCovers, setLoadingCovers] = useState(false);
  const [ragPreview, setRagPreview] = useState(null);
  const [publishHeroFile, setPublishHeroFile] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [publishHeroPreviews, setPublishHeroPreviews] = useState(null);
  const [publishPreviewLoading, setPublishPreviewLoading] = useState(false);
  const [publishPreviewFailed, setPublishPreviewFailed] = useState(false);
  const [exhibitAssets, setExhibitAssets] = useState({});
  const exhibitAssetsRef = useRef(exhibitAssets);
  exhibitAssetsRef.current = exhibitAssets;
  const [enhancingExhibitIndex, setEnhancingExhibitIndex] = useState(null);
  const initialLnatProgress = useMemo(() => readLnatStudioProgress(), []);
  const [examSessionId] = useState(
    () => initialLnatProgress?.sessionId || `exam-${Date.now()}`,
  );
  const [lnatPassage, setLnatPassage] = useState(null);
  const [lnatPassageValidation, setLnatPassageValidation] = useState(null);
  const [lnatQuestions, setLnatQuestions] = useState(null);
  const [lnatQuestionValidation, setLnatQuestionValidation] = useState(null);
  const [lnatCompiled, setLnatCompiled] = useState(null);
  const [lnatDatabase, setLnatDatabase] = useState(null);
  const [lnatVerification, setLnatVerification] = useState(null);
  const [lnatExamination, setLnatExamination] = useState(null);
  const [lnatBusy, setLnatBusy] = useState(false);
  const [lnatActiveStep, setLnatActiveStep] = useState('');
  const [lnatDownloading, setLnatDownloading] = useState(false);
  const [lnatExportMessage, setLnatExportMessage] = useState('');
  const [lnatSavingPartial, setLnatSavingPartial] = useState(false);
  const [lnatPartialSaveMessage, setLnatPartialSaveMessage] = useState('');
  const [lnatPassageNumber, setLnatPassageNumber] = useState(1);
  const [lnatPassageTotal, setLnatPassageTotal] = useState(8);
  const [lnatPassageMeta, setLnatPassageMeta] = useState([]);
  const [lnatRunAllActive, setLnatRunAllActive] = useState(false);
  /** Sync flag for poll loop — full exam must not hit per-step client wait caps. */
  const lnatRunAllActiveRef = useRef(false);
  const lnatAllPassageResultsRef = useRef({});
  const [lnatAllPassageResults, setLnatAllPassageResults] = useState(
    () => initialLnatProgress?.results || {},
  );
  const [lnatFullExam, setLnatFullExam] = useState(
    () => initialLnatProgress?.fullExam || null,
  );
  const [lnatRunAllProgress, setLnatRunAllProgress] = useState('');
  const [examReaderOpen, setExamReaderOpen] = useState(false);
  const [richFields, setRichFields] = useState({
    descriptionHtml: '<p></p>',
    act1Title: '',
    act1Html: '<p></p>',
    act2Title: '',
    act2Html: '<p></p>',
    act3Title: '',
    act3Html: '<p></p>',
  });

  const syncRichFromCase = useCallback((c) => {
    setRichFields({
      descriptionHtml: c?.description || '<p></p>',
      act1Title: c?.act_1?.title || '',
      act1Html: c?.act_1?.content || '<p></p>',
      act2Title: c?.act_2?.title || '',
      act2Html: c?.act_2?.content || '<p></p>',
      act3Title: c?.act_3?.title || '',
      act3Html: c?.act_3?.content || '<p></p>',
    });
  }, []);

  const syncCaseFromRich = useCallback((nextRich) => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      return applyStudioFlags({
        ...prev,
        description: nextRich.descriptionHtml || '<p></p>',
        act_1: { ...prev.act_1, title: nextRich.act1Title, content: nextRich.act1Html || '<p></p>' },
        act_2: { ...prev.act_2, title: nextRich.act2Title, content: nextRich.act2Html || '<p></p>' },
        act_3: { ...prev.act_3, title: nextRich.act3Title, content: nextRich.act3Html || '<p></p>' },
      });
    });
  }, []);

  const selectedCoverForPublish = useMemo(
    () => coverItems.find((x) => x.id === selectedCoverId && x.url) || null,
    [coverItems, selectedCoverId]
  );
  const lnatQuestionList = useMemo(() => {
    if (!lnatQuestions || typeof lnatQuestions !== 'object') return [];
    if (Array.isArray(lnatQuestions.questions)) return lnatQuestions.questions;
    if (Array.isArray(lnatQuestions.subsection?.questions)) return lnatQuestions.subsection.questions;
    if (Array.isArray(lnatQuestions.compiled_subsection?.questions)) return lnatQuestions.compiled_subsection.questions;
    return [];
  }, [lnatQuestions]);

  const lnatCompiledQuestionList = useMemo(() => {
    if (!lnatCompiled || typeof lnatCompiled !== 'object') return [];
    const sub = lnatCompiled.compiled_subsection || lnatCompiled.subsection;
    if (sub && Array.isArray(sub.questions)) return sub.questions;
    if (Array.isArray(lnatCompiled.questions)) return lnatCompiled.questions;
    return [];
  }, [lnatCompiled]);

  const lnatDbQuestionList = useMemo(() => {
    if (!lnatDatabase || typeof lnatDatabase !== 'object') return [];
    if (Array.isArray(lnatDatabase.questions)) return lnatDatabase.questions;
    return [];
  }, [lnatDatabase]);

  const examReaderSource = useMemo(() => {
    if (lnatFullExam && Array.isArray(lnatFullExam.questions) && lnatFullExam.questions.length) {
      return {
        questions: lnatFullExam.questions,
        label: `Full exam · ${lnatFullExam.question_count || lnatFullExam.questions.length} questions`,
      };
    }
    if (lnatDbQuestionList.length) {
      return {
        questions: lnatDbQuestionList,
        label: `Passage ${lnatPassageNumber} · Step 6 (${lnatDbQuestionList.length} questions)`,
      };
    }
    return { questions: [], label: '' };
  }, [lnatFullExam, lnatDbQuestionList, lnatPassageNumber]);

  const lnatVerificationReports = useMemo(() => {
    if (!lnatVerification || typeof lnatVerification !== 'object') return [];
    if (Array.isArray(lnatVerification.questions)) return lnatVerification.questions;
    return [];
  }, [lnatVerification]);

  const lnatExaminationReports = useMemo(() => {
    if (!lnatExamination || typeof lnatExamination !== 'object') return [];
    if (Array.isArray(lnatExamination.questions)) return lnatExamination.questions;
    return [];
  }, [lnatExamination]);

  const lnatQuestionValidationPassed = useMemo(() => {
    const d = String(lnatQuestionValidation?.validation_decision || lnatQuestionValidation?.decision || '').toUpperCase();
    return d === 'PASS';
  }, [lnatQuestionValidation]);

  const lnatPassageLocked = Boolean(lnatPassageValidation);

  const isLnatExam = subject === 'Law' && (industry || '').toUpperCase().includes('LNAT');

  const updateLnatQuestionStyle = useCallback((questionId, patch) => {
    const mutateQ = (q) => {
      const next = { ...q };
      if (patch.stem !== undefined) next.stem = patch.stem;
      if (patch.explanation_html !== undefined) {
        next.explanation_html = patch.explanation_html;
        next.explanation = patch.explanation_html;
      }
      if (patch.optionLabel !== undefined && patch.optionHtml !== undefined) {
        next.options = (q.options || []).map((opt) => {
          const optLabel = String(opt.label || '').toUpperCase();
          if (optLabel === String(patch.optionLabel).toUpperCase()) {
            return { ...opt, text: patch.optionHtml };
          }
          return opt;
        });
      }
      return next;
    };

    if (
      patch.stem !== undefined
      || patch.explanation_html !== undefined
      || (patch.optionLabel !== undefined && patch.optionHtml !== undefined)
    ) {
      setLnatQuestions((prev) => (
        prev ? lnatMutateQuestionsInPayload(prev, questionId, mutateQ) : prev
      ));
      setLnatCompiled((prev) => (
        prev ? lnatMutateQuestionsInPayload(prev, questionId, mutateQ) : prev
      ));
    }

    if (
      patch.stem !== undefined
      || patch.explanation_html !== undefined
      || (patch.optionLabel !== undefined && patch.optionHtml !== undefined)
    ) {
      const extId = lnatExternalIdFromQuestionId(questionId, lnatPassageNumber);
      setLnatDatabase((prev) => {
        if (!prev) return prev;
        return lnatMutateDatabaseRecord(prev, extId, (rec) => {
          const next = { ...rec };
          if (patch.stem !== undefined) next.question_text = lnatWrapStemAsQuestionHtml(patch.stem);
          if (patch.explanation_html !== undefined) next.explanation = patch.explanation_html;
          if (patch.optionLabel !== undefined && patch.optionHtml !== undefined && Array.isArray(rec.answers)) {
            const letter = String(patch.optionLabel).toUpperCase();
            next.answers = rec.answers.map((ans) => (
              String(ans.answer_letter || '').toUpperCase() === letter
                ? { ...ans, answer_text: patch.optionHtml }
                : ans
            ));
          }
          return next;
        });
      });
    }
  }, [lnatPassageNumber]);

  /** Keep full-exam passage bundles in sync with styling edits (export / partial save). */
  useEffect(() => {
    if (!isLnatExam || !lnatPassageNumber) return;
    const bundle = buildLnatCurrentPassageBundle({
      passage: lnatPassage,
      passageValidation: lnatPassageValidation,
      questions: lnatQuestions,
      questionValidation: lnatQuestionValidation,
      compiled: lnatCompiled,
      database: lnatDatabase,
      verification: lnatVerification,
      examination: lnatExamination,
    });
    if (!lnatBundleHasAnyStep(bundle)) return;
    setLnatAllPassageResults((prev) => ({
      ...prev,
      [lnatPassageNumber]: {
        ...(prev[lnatPassageNumber] || {}),
        ...bundle,
      },
    }));
  }, [
    isLnatExam,
    lnatPassageNumber,
    lnatPassage,
    lnatPassageValidation,
    lnatQuestions,
    lnatQuestionValidation,
    lnatCompiled,
    lnatDatabase,
    lnatVerification,
    lnatExamination,
  ]);

  const lnatCurrentQuestionCount = useMemo(() => {
    const row = lnatPassageMeta.find((p) => p.passage_number === lnatPassageNumber);
    return row?.question_count > 0 ? row.question_count : 4;
  }, [lnatPassageMeta, lnatPassageNumber]);

  useEffect(() => {
    lnatAllPassageResultsRef.current = lnatAllPassageResults;
  }, [lnatAllPassageResults]);

  useEffect(() => {
    if (!isLnatExam) return;
    writeLnatStudioProgress({
      sessionId: examSessionId,
      results: lnatAllPassageResults,
      fullExam: lnatFullExam,
      mockExamNumber: lnatMockExamNumber,
      passageTotal: lnatPassageTotal,
      savedAt: Date.now(),
    });
  }, [
    isLnatExam,
    examSessionId,
    lnatAllPassageResults,
    lnatFullExam,
    lnatMockExamNumber,
    lnatPassageTotal,
  ]);

  useEffect(() => {
    if (subject !== 'Law' || !(industry || '').toUpperCase().includes('LNAT')) return;
    examStudioAPI.listLnatPassages().then((res) => {
      const list = Array.isArray(res?.passages) ? res.passages : [];
      if (list.length) {
        setLnatPassageMeta(list);
        setLnatPassageTotal(Math.max(...list.map((p) => p.passage_number)));
      }
    }).catch(() => {});
  }, [subject, industry]);

  useEffect(() => {
    if (!isLnatExam) return;
    if (!LNAT_MODE_OPTIONS.includes(mode)) {
      setMode(mode === 'Exam mode' ? 'Mock exam' : 'Practice by topic');
    }
  }, [isLnatExam, subject, industry]);

  const resetLnatPassageWorkflowState = useCallback(() => {
    setLnatPassage(null);
    setLnatPassageValidation(null);
    setLnatQuestions(null);
    setLnatQuestionValidation(null);
    setLnatCompiled(null);
    setLnatDatabase(null);
    setLnatVerification(null);
    setLnatExamination(null);
  }, []);

  const pollLnatJob = useCallback(async (jobId, step) => {
    const fullExamBuild = lnatRunAllActiveRef.current;
    const maxWaitByStepMs = {
      step1_generate_passage: 6 * 60 * 1000,
      step2_validate_passage: 4 * 60 * 1000,
      // STEP-3 may run four model calls sequentially on the server; allow enough wall time.
      step3_generate_questions: 22 * 60 * 1000,
      step4_validate_questions: 8 * 60 * 1000,
      step5_compile_explanations: 10 * 60 * 1000,
      step6_compile_database: 10 * 60 * 1000,
      step7_verify_questions: 16 * 60 * 1000,
      step8_examine_questions: 16 * 60 * 1000,
    };
    // Full exam: poll until the server job finishes (series run, no client step cap).
    const maxWaitMs = fullExamBuild ? null : (maxWaitByStepMs[step] || 6 * 60 * 1000);
    const fetchStatusWithRetry = async () => {
      const attempts = 5;
      let lastErr;
      for (let a = 0; a < attempts; a += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          return await examStudioAPI.lnatJobStatus(jobId);
        } catch (e) {
          lastErr = e;
          const msg = String(e?.message || e || '');
          const name = String(e?.name || '');
          const transient =
            name === 'AbortError'
            || /timed out|timeout|Failed to fetch|NetworkError|ERR_NAME_NOT_RESOLVED|NAME_NOT_RESOLVED/i.test(
              msg,
            );
          if (!transient || a === attempts - 1) {
            throw e;
          }
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 600 * (a + 1)));
        }
      }
      throw lastErr;
    };
    const startedAt = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (maxWaitMs === null || Date.now() - startedAt < maxWaitMs) {
      // eslint-disable-next-line no-await-in-loop
      await examStudioAPI.ensureAuthFresh();
      // eslint-disable-next-line no-await-in-loop
      const status = await fetchStatusWithRetry();
      if (status.status === 'succeeded') return status.result || {};
      if (status.status === 'failed') throw new Error(status.error || 'LNAT pipeline job failed');
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    const stepLabels = {
      step1_generate_passage: 'Step 1 (passage)',
      step2_validate_passage: 'Step 2 (passage validation)',
      step3_generate_questions: 'Step 3 (questions)',
      step4_validate_questions: 'Step 4 (question validation)',
      step5_compile_explanations: 'Step 5 (compile explanations)',
      step6_compile_database: 'Step 6 (database JSON)',
      step7_verify_questions: 'Step 7 (verification)',
      step8_examine_questions: 'Step 8 (examination)',
    };
    throw new Error(
      `${stepLabels[step] || step} is still running on the server. Wait and retry.`
    );
  }, []);

  const formatLnatError = (err) => {
    const msg = String(err?.message || err || '');
    const name = String(err?.name || '');
    if (name === 'AbortError' || /timed out|timeout/i.test(msg)) {
      return 'Request timed out while waiting for the exam studio API. If generation is still running on the server, wait and retry; otherwise check your API URL and network.';
    }
    if (/ERR_NAME_NOT_RESOLVED|NAME_NOT_RESOLVED|Failed to fetch/i.test(msg)) {
      return 'Cannot reach the API server (DNS/network). Confirm REACT_APP_API_URL points to your running backend (e.g. http://localhost:8080).';
    }
    return msg || 'LNAT step failed';
  };

  const runLnatStep = useCallback(async (step, payload = {}) => {
    setError('');
    setLnatBusy(true);
    setLnatActiveStep(step);
    try {
      await examStudioAPI.ensureAuthFresh();
      const started = await examStudioAPI.startLnatJob(step, payload);
      if (started?.success === false) {
        throw new Error(started.error || 'Failed to start LNAT step.');
      }
      if (!started?.job_id) throw new Error('Failed to start LNAT step.');
      return await pollLnatJob(started.job_id, step);
    } catch (e) {
      throw new Error(formatLnatError(e));
    } finally {
      setLnatBusy(false);
      setLnatActiveStep('');
    }
  }, [pollLnatJob]);

  const lnatPipelineFields = useCallback(
    (passageNum) => ({
      session_id: examSessionId,
      passage_number: passageNum ?? lnatPassageNumber,
      subject,
      exam: industry,
      mode,
      topic_tag: 'law',
      official_reference: '',
      extra_links: [],
      lnat_delivery_mode: lnatDeliveryModeFromMode(mode),
      mock_exam_number: lnatMockExamNumber,
      mock_number: lnatMockExamNumber,
    }),
    [examSessionId, lnatPassageNumber, subject, industry, mode, lnatMockExamNumber],
  );

  const applyLnatPassageBundle = useCallback((passageNum, bundle) => {
    if (!bundle) return;
    setLnatPassageNumber(passageNum);
    setLnatPassage(bundle.step1 ?? null);
    setLnatPassageValidation(bundle.step2 ?? null);
    setLnatQuestions(bundle.step3 ?? null);
    setLnatQuestionValidation(bundle.step4 ?? null);
    setLnatCompiled(bundle.step5 ?? null);
    setLnatDatabase(bundle.step6 ?? null);
    setLnatVerification(bundle.step7 ?? null);
    setLnatExamination(bundle.step8 ?? null);
  }, []);

  const runPassageSteps1Through8 = useCallback(async (passageNum) => {
    setLnatPassageNumber(passageNum);
    const base = { session_id: examSessionId, passage_number: passageNum };
    const existing = lnatAllPassageResultsRef.current[passageNum];

    if (lnatPassageBundleComplete(existing)) {
      applyLnatPassageBundle(passageNum, existing);
      setLnatRunAllProgress(`Passage ${passageNum} — skipped (already complete)`);
      return passageNum;
    }

    let p1 = existing?.step1;
    let p2 = existing?.step2;
    let p3 = existing?.step3;
    let p4 = existing?.step4 ?? null;
    let p5 = existing?.step5;
    let p6 = existing?.step6;
    let p7 = existing?.step7;
    let p8 = existing?.step8;

    if (existing && lnatBundleHasAnyStep(existing)) {
      applyLnatPassageBundle(passageNum, existing);
    } else {
      resetLnatPassageWorkflowState();
    }

    const stashPartialBundle = () => {
      const partial = {};
      if (p1) partial.step1 = p1;
      if (p2) partial.step2 = p2;
      if (p3) partial.step3 = p3;
      if (p4) partial.step4 = p4;
      if (p5) partial.step5 = p5;
      if (p6) partial.step6 = p6;
      if (p7) partial.step7 = p7;
      if (p8) partial.step8 = p8;
      if (!lnatBundleHasAnyStep(partial)) return;
      setLnatAllPassageResults((prev) => ({
        ...prev,
        [passageNum]: { ...(prev[passageNum] || {}), ...partial },
      }));
    };

    try {
      if (!p1) {
        setLnatRunAllProgress(`Passage ${passageNum} — Step 1`);
        p1 = await runLnatStep('step1_generate_passage', {
          ...base,
          ...lnatPipelineFields(passageNum),
        });
        setLnatPassage(p1);
      }
      if (!p2) {
        setLnatRunAllProgress(`Passage ${passageNum} — Step 2`);
        p2 = await runLnatStep('step2_validate_passage', { ...base, step1_data: p1 });
        setLnatPassageValidation(p2);
      }
      if (!p3) {
        setLnatRunAllProgress(`Passage ${passageNum} — Step 3`);
        p3 = await runLnatStep('step3_generate_questions', { ...base, step1_data: p1 });
        setLnatQuestions(p3);
      }
      if (!p4) {
        setLnatRunAllProgress(`Passage ${passageNum} — Step 4`);
        try {
          p4 = await runLnatStep('step4_validate_questions', {
            ...base,
            step1_data: p1,
            step3_data: p3,
          });
          setLnatQuestionValidation(p4);
        } catch (e) {
          p4 = { validation_decision: 'FAIL', decision: 'FAIL' };
          setLnatQuestionValidation(p4);
        }
      }
      if (!p5) {
        setLnatRunAllProgress(`Passage ${passageNum} — Step 5`);
        p5 = await runLnatStep('step5_compile_explanations', {
          ...base,
          step4_data: p4 || { validation_decision: 'FAIL', decision: 'FAIL' },
          step3_data: p3,
          pipeline_test_mode: true,
        });
        setLnatCompiled(p5);
      }
      if (!p6) {
        setLnatRunAllProgress(`Passage ${passageNum} — Step 6`);
        p6 = await runLnatStep('step6_compile_database', {
          ...lnatPipelineFields(passageNum),
          step5_data: p5,
        });
        setLnatDatabase(p6);
      }
      if (!p7) {
        setLnatRunAllProgress(`Passage ${passageNum} — Step 7`);
        p7 = await runLnatStep('step7_verify_questions', { ...base, step6_data: p6 });
        setLnatVerification(p7);
      }
      if (!p8) {
        setLnatRunAllProgress(`Passage ${passageNum} — Step 8`);
        p8 = await runLnatStep('step8_examine_questions', { ...base, step6_data: p6 });
        setLnatExamination(p8);
      }
      const bundle = {
        step1: p1,
        step2: p2,
        step3: p3,
        step4: p4 || { validation_decision: 'FAIL', decision: 'FAIL' },
        step5: p5,
        step6: p6,
        step7: p7,
        step8: p8,
      };
      setLnatAllPassageResults((prev) => ({ ...prev, [passageNum]: bundle }));
      setLnatRunAllProgress(`Passage ${passageNum} — complete`);
      return passageNum;
    } catch (e) {
      stashPartialBundle();
      throw e;
    }
  }, [
    runLnatStep,
    examSessionId,
    resetLnatPassageWorkflowState,
    lnatPipelineFields,
    applyLnatPassageBundle,
  ]);

  const handleLnatRunAllPassages = useCallback(async () => {
    setError('');
    lnatRunAllActiveRef.current = true;
    setLnatRunAllActive(true);
    try {
      let mergedResults = { ...lnatAllPassageResultsRef.current };

      setLnatRunAllProgress('Loading saved session progress…');
      try {
        await examStudioAPI.ensureAuthFresh();
        const serverProgress = await examStudioAPI.getLnatSessionProgress(examSessionId);
        if (serverProgress?.passages) {
          mergedResults = mergeLnatAllPassageResults(mergedResults, serverProgress.passages);
          lnatAllPassageResultsRef.current = mergedResults;
          setLnatAllPassageResults(mergedResults);
        }
      } catch {
        /* browser-only progress still allows resume */
      }

      const doneAfterLoad = lnatCompletedPassageNumbers(mergedResults, lnatPassageTotal);
      const startPassage = lnatFirstIncompletePassage(mergedResults, lnatPassageTotal) || 1;
      if (doneAfterLoad.length) {
        setLnatRunAllProgress(
          `Resuming full exam — ${doneAfterLoad.length} passage(s) already complete, starting at passage ${startPassage}…`,
        );
      } else {
        setLnatRunAllProgress('Starting full exam build…');
      }

      for (let p = startPassage; p <= lnatPassageTotal; p += 1) {
        if (lnatPassageBundleComplete(mergedResults[p])) {
          setLnatRunAllProgress(`Passage ${p} of ${lnatPassageTotal} — skipped (already complete)`);
          // eslint-disable-next-line no-continue
          continue;
        }
        setLnatRunAllProgress(`Full exam: passage ${p} of ${lnatPassageTotal} (Steps 1–8)…`);
        // eslint-disable-next-line no-await-in-loop
        await runPassageSteps1Through8(p);
        mergedResults = { ...lnatAllPassageResultsRef.current };
      }
      setLnatRunAllProgress('Merging all passages into one exam file…');
      await examStudioAPI.ensureAuthFresh();
      const assembled = await examStudioAPI.assembleLnatFullExam(examSessionId, {
        mode,
        lnat_delivery_mode: lnatDeliveryModeFromMode(mode),
        mock_exam_number: lnatMockExamNumber,
      });
      if (assembled?.exam) {
        setLnatFullExam(assembled.exam);
      }
      setLnatRunAllProgress(
        `Full exam ready — ${lnatPassageTotal} passages, Steps 1–8 each (${lnatPassageTotal * 8} pipeline steps)`,
      );
    } catch (e) {
      setError(lnatBuildStoppedUserMessage(e));
      setLnatRunAllProgress('Build stopped — save partial progress or click Build full exam again to resume.');
    } finally {
      lnatRunAllActiveRef.current = false;
      setLnatRunAllActive(false);
    }
  }, [lnatPassageTotal, runPassageSteps1Through8, examSessionId, mode, lnatMockExamNumber]);

  const handleLnatClearFullExamProgress = useCallback(() => {
    if (lnatRunAllActive || lnatBusy) return;
    setLnatAllPassageResults({});
    setLnatFullExam(null);
    lnatAllPassageResultsRef.current = {};
    setLnatRunAllProgress('');
    setLnatPartialSaveMessage('');
    resetLnatPassageWorkflowState();
    try {
      sessionStorage.removeItem(LNAT_STUDIO_PROGRESS_KEY);
    } catch {
      /* ignore */
    }
  }, [lnatRunAllActive, lnatBusy, resetLnatPassageWorkflowState]);

  const handleLnatBuildCurrentPassage = useCallback(async () => {
    setError('');
    try {
      await runPassageSteps1Through8(lnatPassageNumber);
    } catch (e) {
      setError(e.message || String(e));
      setLnatRunAllProgress('');
    }
  }, [runPassageSteps1Through8, lnatPassageNumber]);

  useEffect(() => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      const next = { ...prev, subject, industry };
      if (
        prev.subject === subject
        && prev.industry === industry
        && prev.is_original === true
        && prev.is_locked === false
      ) {
        return prev;
      }
      return applyStudioFlags(next);
    });
  }, [subject, industry]);

  useEffect(() => {
    const sourceUrl = selectedCoverForPublish?.url || '';
    if (!publishHeroFile && !sourceUrl) {
      setPublishHeroPreviews(null);
      setPublishPreviewLoading(false);
      setPublishPreviewFailed(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = '';
    const imageSource = sourceUrl || URL.createObjectURL(publishHeroFile);
    if (!sourceUrl) objectUrl = imageSource;
    setPublishHeroPreviews(null);
    setPublishPreviewFailed(false);
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      try {
        const list = CASE_PUBLISH_SIZES.map(([w, h]) => ({
          key: `${w}x${h}`,
          w,
          h,
          src: canvasCropToDataUrl(img, w, h),
        })).filter((row) => row.src);
        setPublishHeroPreviews(list.length ? list : null);
        if (!list.length) setPublishPreviewFailed(true);
      } catch {
        setPublishHeroPreviews(null);
        setPublishPreviewFailed(true);
      }
      setPublishPreviewLoading(false);
    };
    img.onerror = () => {
      if (!cancelled) {
        setPublishHeroPreviews(null);
        setPublishPreviewLoading(false);
        setPublishPreviewFailed(true);
      }
    };
    img.src = imageSource;
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [publishHeroFile, selectedCoverForPublish]);

  const localFallbackPromptPlan = useMemo(() => {
    if (!caseJson) return [];
    const base = caseJson.title || 'case';
    return [
      { id: 'cover-main', label: 'Main cover', prompt: promptOverride.trim() || null },
      { id: 'cover-act1', label: 'Act 1 scene', prompt: `Act I scene from ${base}. ${stripHtml(caseJson.act_1?.content).slice(0, 600)} Cinematic photorealistic 16:9, no text.` },
      { id: 'cover-act2', label: 'Act 2 scene', prompt: `Act II scene from ${base}. ${stripHtml(caseJson.act_2?.content).slice(0, 600)} Cinematic photorealistic 16:9, no text.` },
      { id: 'cover-act3', label: 'Act 3 scene', prompt: `Act III dilemma scene from ${base}. ${stripHtml(caseJson.act_3?.content).slice(0, 600)} Cinematic photorealistic 16:9, no text.` },
    ];
  }, [caseJson, promptOverride]);

  const handleLnatGeneratePassage = useCallback(async () => {
    try {
      const result = await runLnatStep('step1_generate_passage', lnatPipelineFields(lnatPassageNumber));
      setLnatPassage(result);
      setLnatPassageValidation(null);
      setLnatQuestions(null);
      setLnatQuestionValidation(null);
      setLnatCompiled(null);
      setLnatDatabase(null);
      setLnatVerification(null);
      setLnatExamination(null);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [runLnatStep, lnatPipelineFields, lnatPassageNumber]);

  const handleLnatValidatePassage = useCallback(async () => {
    if (!lnatPassage) return;
    try {
      const result = await runLnatStep('step2_validate_passage', {
        session_id: examSessionId,
        passage_number: lnatPassageNumber,
        step1_data: lnatPassage,
      });
      setLnatPassageValidation(result);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [runLnatStep, examSessionId, lnatPassageNumber, lnatPassage]);

  const handleLnatGenerateQuestions = useCallback(async () => {
    if (!lnatPassage) return;
    try {
      const result = await runLnatStep('step3_generate_questions', {
        session_id: examSessionId,
        passage_number: lnatPassageNumber,
        step1_data: lnatPassage,
      });
      setLnatQuestions(result);
      setLnatQuestionValidation(null);
      setLnatCompiled(null);
      setLnatDatabase(null);
      setLnatVerification(null);
      setLnatExamination(null);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [runLnatStep, examSessionId, lnatPassageNumber, lnatPassage]);

  const handleLnatValidateQuestions = useCallback(async () => {
    if (!lnatPassage || !lnatQuestions) return;
    try {
      const result = await runLnatStep('step4_validate_questions', {
        session_id: examSessionId,
        passage_number: lnatPassageNumber,
        step1_data: lnatPassage,
        step3_data: lnatQuestions,
      });
      setLnatQuestionValidation(result);
      setLnatCompiled(null);
      setLnatDatabase(null);
      setLnatVerification(null);
      setLnatExamination(null);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [runLnatStep, examSessionId, lnatPassageNumber, lnatPassage, lnatQuestions]);

  const handleLnatCompileExplanations = useCallback(async () => {
    if (!lnatQuestions) {
      setError('Generate questions (Step 3) before compiling explanations.');
      return;
    }
    try {
      const result = await runLnatStep('step5_compile_explanations', {
        session_id: examSessionId,
        passage_number: lnatPassageNumber,
        step4_data: lnatQuestionValidation || {
          validation_decision: 'FAIL',
          decision: 'FAIL',
        },
        step3_data: lnatQuestions,
        pipeline_test_mode: true,
      });
      setLnatCompiled(result);
      setLnatDatabase(null);
      setLnatVerification(null);
      setLnatExamination(null);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [runLnatStep, examSessionId, lnatPassageNumber, lnatQuestionValidation, lnatQuestions]);

  const handleLnatCompileDatabase = useCallback(async () => {
    if (!lnatCompiled) return;
    try {
      const result = await runLnatStep('step6_compile_database', {
        ...lnatPipelineFields(lnatPassageNumber),
        step5_data: lnatCompiled,
      });
      setLnatDatabase(result);
      setLnatVerification(null);
      setLnatExamination(null);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [runLnatStep, lnatPipelineFields, lnatPassageNumber, lnatCompiled]);

  const handleLnatVerifyQuestions = useCallback(async () => {
    if (!lnatDatabase) return;
    try {
      const result = await runLnatStep('step7_verify_questions', {
        session_id: examSessionId,
        passage_number: lnatPassageNumber,
        step6_data: lnatDatabase,
      });
      setLnatVerification(result);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [runLnatStep, examSessionId, lnatPassageNumber, lnatDatabase]);

  const handleLnatExamineQuestions = useCallback(async () => {
    if (!lnatDatabase) return;
    try {
      const result = await runLnatStep('step8_examine_questions', {
        session_id: examSessionId,
        passage_number: lnatPassageNumber,
        step6_data: lnatDatabase,
      });
      setLnatExamination(result);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, [runLnatStep, examSessionId, lnatPassageNumber, lnatDatabase]);

  const handleLnatExportOutputs = useCallback(async () => {
    setError('');
    setLnatExportMessage('');
    setLnatDownloading(true);
    try {
      const res = await examStudioAPI.exportLnatSessionToExamBuilderJson(
        examSessionId,
        lnatPipelineFields(lnatPassageNumber),
      );
      if (!res?.success) {
        throw new Error(res?.error || 'Export failed');
      }
      const sha = res.commit_sha ? ` (${res.commit_sha.slice(0, 7)})` : '';
      const pushNote = res.pushed
        ? `Pushed to GitHub${sha}.`
        : 'Committed locally (push skipped — no token on server).';
      const pathNote = res.github_url || res.relative_path || res.export_folder_name;
      setLnatExportMessage(`${pushNote} ${res.file_count} file(s) → ${pathNote}`);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLnatDownloading(false);
    }
  }, [examSessionId, lnatPipelineFields, lnatPassageNumber]);

  const lnatPassageSyncPayload = useCallback(
    () => buildLnatPassageSyncMap(
      lnatAllPassageResults,
      lnatPassageNumber,
      buildLnatCurrentPassageBundle({
        passage: lnatPassage,
        passageValidation: lnatPassageValidation,
        questions: lnatQuestions,
        questionValidation: lnatQuestionValidation,
        compiled: lnatCompiled,
        database: lnatDatabase,
        verification: lnatVerification,
        examination: lnatExamination,
      }),
    ),
    [
      lnatAllPassageResults,
      lnatPassageNumber,
      lnatPassage,
      lnatPassageValidation,
      lnatQuestions,
      lnatQuestionValidation,
      lnatCompiled,
      lnatDatabase,
      lnatVerification,
      lnatExamination,
    ],
  );

  const handleLnatSavePartialProgress = useCallback(async () => {
    setError('');
    setLnatPartialSaveMessage('');
    const passages = lnatPassageSyncPayload();
    const passageKeys = Object.keys(passages);
    if (!passageKeys.length) {
      setError('No passage outputs to save yet. Complete at least one step on a passage first.');
      return;
    }
    setLnatSavingPartial(true);
    try {
      const res = await examStudioAPI.saveLnatPartialProgressToRepo(examSessionId, {
        ...lnatPipelineFields(lnatPassageNumber),
        passages,
        partial: passageKeys.length < lnatPassageTotal || !lnatFullExam,
      });
      if (!res?.success) {
        throw new Error(res?.error || 'Partial save failed');
      }
      const sha = res.commit_sha ? ` (${res.commit_sha.slice(0, 7)})` : '';
      const pushNote = res.pushed
        ? `Pushed partial export to GitHub${sha}.`
        : 'Committed locally (push skipped — no token on server).';
      const pathNote = res.github_url || res.relative_path || res.export_folder_name;
      const syncNote = res.sync?.synced_passages?.length
        ? ` Synced passage(s): ${res.sync.synced_passages.join(', ')}.`
        : '';
      setLnatPartialSaveMessage(
        `${pushNote}${syncNote} ${res.file_count} file(s) → ${pathNote}`,
      );
      if (res.partial && !lnatFullExam) {
        try {
          await examStudioAPI.ensureAuthFresh();
          const assembled = await examStudioAPI.assembleLnatFullExam(examSessionId, {
            mode,
            lnat_delivery_mode: lnatDeliveryModeFromMode(mode),
            mock_exam_number: lnatMockExamNumber,
            partial: true,
          });
          if (assembled?.exam) {
            setLnatFullExam(assembled.exam);
          }
        } catch {
          /* assemble is best-effort when only some passages have Step 6 */
        }
      }
    } catch (e) {
      setError(lnatBuildStoppedUserMessage(e));
    } finally {
      setLnatSavingPartial(false);
    }
  }, [
    examSessionId,
    lnatPipelineFields,
    lnatPassageNumber,
    lnatPassageSyncPayload,
    lnatPassageTotal,
    lnatFullExam,
    mode,
    lnatMockExamNumber,
  ]);

  const pollOneCover = async (taskId) => {
    for (let i = 0; i < 90; i += 1) {
      const res = await studioCasesAPI.coverStatus(taskId);
      const st = (res.status || '').toUpperCase();
      if (st === 'SUCCEEDED' && res.output_urls && res.output_urls.length) return res.output_urls[0];
      if (st === 'FAILED' || st === 'CANCELED') throw new Error(`Runway task ${st}`);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 4000));
    }
    throw new Error('Cover generation timed out');
  };

  const generateAllFourCovers = async () => {
    if (!caseJson) return;
    setError('');
    setLoadingCovers(true);
    setCoverItems([]);
    setSelectedCoverId('');
    try {
      let promptPlan = [];
      const promptPack = await studioCasesAPI.coverPrompts(caseJson);
      if (promptPack?.success && promptPack?.prompts) {
        promptPlan = [
          { id: 'cover-main', label: 'Main cover', prompt: promptPack.prompts.whole_case || '' },
          { id: 'cover-act1', label: 'Act 1 scene', prompt: promptPack.prompts.act_1 || '' },
          { id: 'cover-act2', label: 'Act 2 scene', prompt: promptPack.prompts.act_2 || '' },
          { id: 'cover-act3', label: 'Act 3 scene', prompt: promptPack.prompts.act_3 || '' },
        ];
      } else {
        promptPlan = localFallbackPromptPlan;
      }
      if (promptOverride.trim()) {
        promptPlan[0] = { ...promptPlan[0], prompt: promptOverride.trim() };
      }

      const out = [];
      for (const item of promptPlan) {
        // one-by-one to avoid timeout pressure
        // eslint-disable-next-line no-await-in-loop
        const start = await studioCasesAPI.startCover(caseJson, item.prompt);
        if (!start.success || !start.task_id) throw new Error(start.error || `Failed starting ${item.label}`);
        // eslint-disable-next-line no-await-in-loop
        const url = await pollOneCover(start.task_id);
        out.push({ ...item, url });
        setCoverItems([...out]);
      }
      setSelectedCoverId((out.find((x) => x.id === 'cover-main') || out[0] || {}).id || '');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoadingCovers(false);
    }
  };

  const handleTitleSuggestions = async () => {
    if (!caseJson) return;
    setLoadingTitles(true);
    setError('');
    try {
      const res = await studioCasesAPI.titleSuggestions(caseJson);
      const list = Array.isArray(res.titles) ? res.titles : [];
      setTitleSuggestions(list.slice(0, 3));
    } catch (e) {
      setError(e.message || 'Title suggestions failed');
    } finally {
      setLoadingTitles(false);
    }
  };

  const handleFinalLoglineSuggestions = async () => {
    if (!caseJson) return;
    setLoadingFinalLoglines(true);
    setError('');
    try {
      const fromFinal = htmlToPlainText(richFields.descriptionHtml);
      const preFormHint = (logline || '').trim();
      const draftLogline = [fromFinal, preFormHint].filter(Boolean).join('\n\n');
      const res = await studioCasesAPI.loglineSuggestions({
        title: (caseJson.title || '').trim(),
        subject,
        industry,
        cliffhanger: '',
        draft_logline: draftLogline,
        domain_notes: domainNotes,
        media_url: mediaUrl.trim(),
      });
      const list = Array.isArray(res.loglines) ? res.loglines : [];
      setFinalLoglineSuggestions(list.slice(0, 3));
    } catch (e) {
      setError(e.message || 'Logline suggestions failed');
    } finally {
      setLoadingFinalLoglines(false);
    }
  };

  const selectCover = (id) => {
    setSelectedCoverId(id);
    setPublishHeroFile(null);
  };

  const downloadJson = async () => {
    if (!caseJson) return;
    try {
      const filename = `${slugify(caseJson.title)}.json`;
      const blob = await studioCasesAPI.downloadCaseBlob(caseJson, filename);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setError(e.message || 'Case download failed');
    }
  };

  const downloadSelectedImage = async () => {
    const selected = coverItems.find((x) => x.id === selectedCoverId && x.url);
    if (!selected) return;
    try {
      const blob = await studioCasesAPI.downloadCoverBlobWithFilename(selected.url, `${slugify(caseJson?.title)}-${selected.id}.png`);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${slugify(caseJson?.title)}-${selected.id}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setError(e.message || 'Image download failed');
    }
  };

  const updateRichField = (key, val) => {
    const next = { ...richFields, [key]: val };
    setRichFields(next);
    syncCaseFromRich(next);
  };

  const getExhibits = () => (Array.isArray(caseJson?.exhibits) ? caseJson.exhibits : []);

  const updateExhibitAt = (idx, patch) => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      const exhibits = Array.isArray(prev.exhibits) ? [...prev.exhibits] : [];
      while (exhibits.length <= idx) {
        exhibits.push({
          title: 'Exhibit',
          description: '<p></p>',
          is_HTML: false,
          HTML_data: '',
          is_image: false,
          image_url: '',
          notes: '<p>-</p>',
          source: '<p>[ ]</p>',
        });
      }
      const merged = { ...exhibits[idx], ...patch };
      const mergedSanitized = sanitizeExhibitDisplayFields(merged);
      const pending = exhibitAssetsRef.current[idx]?.imageFiles?.filter(Boolean).length || 0;
      const auto = deriveExhibitAutoFields(mergedSanitized, pending);
      exhibits[idx] = { ...mergedSanitized, ...auto };
      return { ...prev, exhibits };
    });
  };

  useEffect(() => {
    setCaseJson((prev) => {
      if (!prev?.exhibits?.length) return prev;
      let changed = false;
      const exhibits = prev.exhibits.map((ex, idx) => {
        const pending = exhibitAssets[idx]?.imageFiles?.filter(Boolean).length || 0;
        const auto = deriveExhibitAutoFields(ex, pending);
        if (ex.is_HTML === auto.is_HTML && ex.is_image === auto.is_image) return ex;
        changed = true;
        return { ...ex, ...auto };
      });
      return changed ? { ...prev, exhibits } : prev;
    });
  }, [exhibitAssets]);

  const addExhibit = () => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      const exhibits = Array.isArray(prev.exhibits) ? [...prev.exhibits] : [];
      exhibits.push({
        title: `Exhibit ${exhibits.length + 1}`,
        description: '<p></p>',
        is_HTML: false,
        HTML_data: '',
        is_image: false,
        image_url: '',
        notes: '<p>-</p>',
        source: '<p>[ ]</p>',
      });
      return { ...prev, exhibits };
    });
  };

  const deleteExhibit = (idx) => {
    setCaseJson((prev) => {
      if (!prev) return prev;
      const exhibits = Array.isArray(prev.exhibits) ? [...prev.exhibits] : [];
      if (idx < 0 || idx >= exhibits.length) return prev;
      exhibits.splice(idx, 1);
      return { ...prev, exhibits };
    });
    setExhibitAssets((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const oldIdx = Number(k);
        if (Number.isNaN(oldIdx) || oldIdx === idx) return;
        next[oldIdx > idx ? oldIdx - 1 : oldIdx] = v;
      });
      return next;
    });
  };

  const enhanceExhibit = async (idx) => {
    if (!caseJson) return;
    const ex = getExhibits()[idx];
    if (!ex) return;
    setError('');
    setEnhancingExhibitIndex(idx);
    try {
      const fd = new FormData();
      fd.append('exhibit_json', JSON.stringify(ex));
      fd.append('case_json', JSON.stringify(caseJson));
      const sourceUrls = [];
      const sourceUrl = (exhibitAssets[idx]?.sourceUrl || '').trim();
      if (sourceUrl) sourceUrls.push(sourceUrl);
      if (sourceUrls.length) fd.append('source_urls', Array.from(new Set(sourceUrls)).join('\n'));

      const srcFile = exhibitAssets[idx]?.sourceFile || null;
      if (srcFile) fd.append('source_file', srcFile, srcFile.name || `exhibit-${idx + 1}-source`);
      const imgFiles = exhibitAssets[idx]?.imageFiles || [];
      imgFiles.forEach((f, i) => {
        if (f) fd.append('source_file', f, f.name || `exhibit-${idx + 1}-image-${i + 1}`);
      });
      const res = await studioCasesAPI.enhanceExhibit(fd);
      if (!res?.success || !res?.exhibit) throw new Error(res?.error || 'Exhibit enhancement failed');
      updateExhibitAt(idx, res.exhibit);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setEnhancingExhibitIndex(null);
    }
  };

  const handlePublishCaseAndMedia = async () => {
    if (!caseJson) return;
    const selectedUrl = selectedCoverForPublish?.url || '';
    if (!selectedUrl && !publishHeroFile) return;
    setError('');
    setPublishResult(null);
    setPublishing(true);
    try {
      const payload = applyStudioFlags({ ...caseJson });
      const fd = new FormData();
      fd.append('case_json', JSON.stringify(payload));
      fd.append('creator_name', creatorName.trim());
      if (selectedUrl) {
        fd.append('hero_image_url', selectedUrl);
      } else if (publishHeroFile) {
        fd.append('hero_image', publishHeroFile, publishHeroFile.name || 'hero.png');
      }
      Object.entries(exhibitAssets).forEach(([k, v]) => {
        const idx = Number(k);
        if (Number.isNaN(idx) || !v) return;
        if (v.imageFiles && v.imageFiles.length) {
          v.imageFiles.forEach((f) => fd.append(`exhibit_image_${idx}`, f, f.name || `exhibit-${idx + 1}.png`));
        }
      });
      const res = await studioCasesAPI.publishCaseWithMedia(fd);
      if (!res.success) {
        const issues = Array.isArray(res.issues) ? res.issues.join('\n') : '';
        throw new Error([res.error || 'Publish failed', issues].filter(Boolean).join('\n'));
      }
      setPublishResult(res);
      if (res.exhibit_image_urls && typeof res.exhibit_image_urls === 'object') {
        setCaseJson((prev) => {
          if (!prev || !Array.isArray(prev.exhibits)) return prev;
          const exhibits = prev.exhibits.map((row, i) => {
            const urls = res.exhibit_image_urls[String(i)];
            if (!urls || !urls.length) return row;
            return {
              ...row,
              is_image: true,
              image_url: urls[0],
            };
          });
          return applyStudioFlags({ ...prev, exhibits });
        });
      }
    } catch (e) {
      const msg = e.message || String(e);
      const issues = e.data?.issues;
      const extra = Array.isArray(issues) && issues.length ? `\n${issues.join('\n')}` : '';
      setError(`${msg}${extra}`);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div style={styles.page}>
      <style>{STUDIO_CASE_BODY_HTML_CSS}</style>
      <div style={styles.inner}>
        <h1 style={styles.h1}>Exam Studio</h1>
        {error ? <div style={styles.err}>{error}</div> : null}

        <div style={styles.row}>
          <div style={styles.rowItem}>
            <label style={styles.label}>Subject</label>
            <select
              style={styles.input}
              value={subject}
              onChange={(e) => {
                const nextSubject = e.target.value;
                const suggestions = EXAM_SUGGESTIONS_BY_SUBJECT[nextSubject] || [];
                setSubject(nextSubject);
                if (industry !== 'General' && suggestions.length && !suggestions.includes(industry)) {
                  setIndustry(suggestions[0]);
                }
              }}
            >
              {SUBJECT_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div style={styles.rowItem}>
            <label style={styles.label}>Exams &amp; qualifications</label>
            <select
              style={styles.input}
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
            >
              <option value="General">General</option>
              {(EXAM_SUGGESTIONS_BY_SUBJECT[subject] || []).map((it) => (
                <option key={it} value={it}>{it}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={styles.row}>
          <div style={styles.rowItem}>
            <label style={styles.label}>Difficulty</label>
            <select
              style={styles.input}
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              disabled={isLnatExam ? mode === 'Mock exam' : mode === 'Exam mode'}
              title={
                (isLnatExam ? mode === 'Mock exam' : mode === 'Exam mode')
                  ? 'Difficulty is fixed in Mock exam / Exam mode; switch to Practice to change it.'
                  : undefined
              }
            >
              {DIFFICULTY_OPTIONS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div style={styles.rowItem}>
            <label style={styles.label}>Mode</label>
            <select
              style={styles.input}
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              {(isLnatExam ? LNAT_MODE_OPTIONS : MODE_OPTIONS).map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div style={styles.rowItem}>
            {lnatDeliveryModeFromMode(mode) === 'mock_exam' ? (
              <>
                <label style={styles.label}>Exam number</label>
                <select
                  style={styles.input}
                  value={lnatMockExamNumber}
                  onChange={(e) => setLnatMockExamNumber(Number(e.target.value))}
                  title="Paper code in Step 6 IDs (e.g. 1 → LNAT-0001-p2-sa-q3)"
                >
                  {Array.from({ length: 1000 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label style={styles.label}>Creator Nickname</label>
                <input
                  style={styles.input}
                  value={creatorName}
                  onChange={(e) => setCreatorName(e.target.value)}
                  placeholder="Creator Name"
                />
              </>
            )}
          </div>
        </div>
        {isLnatExam ? (
          <div style={styles.card}>
            <div style={{ ...styles.sectionTitle, ...styles.sectionTitleFirst }}>
              {`LNAT full exam (${lnatPassageTotal} passages)`}
            </div>
            <div style={{ ...styles.small, marginTop: 6 }}>
              {lnatPassageStatusLine(lnatPassageNumber, lnatPassageTotal, {
                runAllProgress: lnatRunAllProgress,
                activeStep: lnatActiveStep,
                busy: lnatBusy,
              })}
            </div>
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <label style={{ ...styles.label, marginBottom: 0 }}>
                Working passage
                <select
                  style={{ ...styles.input, marginLeft: 8, width: 'auto', display: 'inline-block' }}
                  value={lnatPassageNumber}
                  disabled={lnatBusy || lnatRunAllActive}
                  onChange={(e) => {
                    const num = Number(e.target.value);
                    setLnatPassageNumber(num);
                    const bundle = lnatAllPassageResults[num];
                    if (bundle && lnatBundleHasAnyStep(bundle)) {
                      applyLnatPassageBundle(num, bundle);
                    } else {
                      resetLnatPassageWorkflowState();
                    }
                  }}
                >
                  {Array.from({ length: lnatPassageTotal }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{`Passage ${n}`}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                style={{ ...styles.btn, ...(lnatBusy || lnatRunAllActive ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive}
                onClick={handleLnatRunAllPassages}
                title={`Run Steps 1–8 for each passage (skips completed, resumes incomplete), then merge Step 6 into one full exam file`}
              >
                {lnatRunAllActive
                  ? 'Building full exam…'
                  : `Build full exam (${lnatPassageTotal} passages × Steps 1–8)`}
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...(lnatBusy || lnatRunAllActive ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive}
                onClick={handleLnatClearFullExamProgress}
                title="Clear in-browser progress and server session files for this session id before a new paper"
              >
                Start fresh
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnPrimary, ...(lnatBusy || lnatRunAllActive ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive}
                onClick={handleLnatBuildCurrentPassage}
                title={`Run Steps 1–8 for passage ${lnatPassageNumber} only`}
              >
                {lnatBusy && !lnatRunAllActive
                  ? `Building passage ${lnatPassageNumber}…`
                  : 'Build question'}
              </button>
            </div>
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                style={{ ...styles.btn, ...styles.btnPrimary, ...(lnatBusy ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive}
                onClick={handleLnatGeneratePassage}
              >
                {lnatBusy && lnatActiveStep === 'step1_generate_passage' ? 'Running Step 1...' : 'Generate passage'}
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...(lnatBusy || !lnatPassage ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive || !lnatPassage}
                onClick={handleLnatValidatePassage}
              >
                Validate passage
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...(lnatBusy || !lnatPassage ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive || !lnatPassage}
                onClick={handleLnatGenerateQuestions}
              >
                {`Generate ${lnatCurrentQuestionCount} question${lnatCurrentQuestionCount === 1 ? '' : 's'}`}
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...(lnatBusy || !lnatQuestions ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive || !lnatQuestions}
                onClick={handleLnatValidateQuestions}
              >
                Validate questions
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...(lnatBusy || !lnatQuestions ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive || !lnatQuestions}
                onClick={handleLnatCompileExplanations}
                title="Pipeline test mode: compiles even when Step 4 validation failed (uses Step 3 fallback if needed)"
              >
                {lnatBusy && lnatActiveStep === 'step5_compile_explanations'
                  ? 'Compiling explanations…'
                  : 'Compile explanations'}
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...(lnatBusy || !lnatCompiled ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive || !lnatCompiled}
                onClick={handleLnatCompileDatabase}
              >
                {lnatBusy && lnatActiveStep === 'step6_compile_database'
                  ? 'Compiling database JSON…'
                  : 'Compile database JSON'}
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...(lnatBusy || !lnatDatabase ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive || !lnatDatabase}
                onClick={handleLnatVerifyQuestions}
              >
                {lnatBusy && lnatActiveStep === 'step7_verify_questions'
                  ? 'Verifying questions…'
                  : 'Verify questions'}
              </button>
              <button
                type="button"
                style={{ ...styles.btn, ...(lnatBusy || !lnatDatabase ? styles.btnDisabled : {}) }}
                disabled={lnatBusy || lnatRunAllActive || !lnatDatabase}
                onClick={handleLnatExamineQuestions}
              >
                {lnatBusy && lnatActiveStep === 'step8_examine_questions'
                  ? 'Examining fidelity…'
                  : 'Examine fidelity'}
              </button>
              <button
                type="button"
                style={{
                  ...styles.btn,
                  ...(lnatBusy || lnatDownloading || !lnatPassage ? styles.btnDisabled : {}),
                }}
                disabled={lnatBusy || lnatDownloading || lnatRunAllActive || !lnatPassage}
                onClick={handleLnatExportOutputs}
                title="Write passage folders and full exam JSON to ExamBuilderJson/LAW/LNAT and git commit"
              >
                {lnatDownloading ? 'Adding…' : 'ADD to Repository'}
              </button>
            {lnatExportMessage ? (
              <div style={{ marginTop: 8, fontSize: '0.875rem', color: 'var(--iv-success)' }}>{lnatExportMessage}</div>
            ) : null}
            </div>
            <LnatRunAllOutputsPanel
              results={lnatAllPassageResults}
              fullExam={lnatFullExam}
              onOpenPassage={applyLnatPassageBundle}
              onSavePartial={handleLnatSavePartialProgress}
              savingPartial={lnatSavingPartial}
              savePartialMessage={lnatPartialSaveMessage}
            />
            {lnatPassage ? (
              <div style={{ marginTop: 10 }}>
                <label style={styles.label}>
                  Generated passage (Step 1)
                  {lnatIsTwoPassageStep1(lnatPassage) ? ' — Passages A & B' : ''}
                </label>
                {(() => {
                  const wc = lnatStep1WordCount(lnatPassage);
                  const outOfRange = wc > 0 && (wc < 400 || wc > 700);
                  return (
                    <div style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
                      {wc} words in passage text
                      {outOfRange ? ' — outside LNAT range (400–700); regenerate Step 1' : ' (target ~450–650)'}
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <LnatPassageFields
                      passage={lnatPassage}
                      locked={lnatPassageLocked}
                      onChange={setLnatPassage}
                    />
                  </div>
                </div>
                <div style={styles.small}>
                  {lnatPassageLocked
                    ? `Passage wording is locked after validation (${String(lnatPassageValidation?.decision || lnatPassageValidation?.validation_decision || 'run')}). Use Preview / Edit styling for formatting; changes sync to export and partial save. Regenerate passage for new content.`
                    : 'Passage text is editable until you run Validate passage. After validation, only styling can be changed.'}
                </div>
                <details style={{ marginTop: 8 }}>
                  <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>
                    Passage output JSON (show/hide)
                  </summary>
                  <pre style={{ ...styles.preview, maxHeight: 260 }}>
                    {JSON.stringify(lnatPassage, null, 2)}
                  </pre>
                </details>
              </div>
            ) : null}
            {lnatPassageValidation ? (
              <details style={{ marginTop: 8 }}>
                <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>
                  Passage validation JSON (show/hide) — {String(lnatPassageValidation?.decision || lnatPassageValidation?.validation_decision || '')}
                </summary>
                <pre style={{ ...styles.preview, maxHeight: 220 }}>{JSON.stringify(lnatPassageValidation, null, 2)}</pre>
              </details>
            ) : null}
            {lnatQuestionList.length ? (
              <div style={{ marginTop: 10 }}>
                <div style={styles.small}>
                  Questions generated: {lnatQuestionList.length}
                  {' · '}
                  Preview shows the question as generated; Edit styling for spacing, bold, and line breaks (synced to Step 3–6 and export).
                </div>
                {lnatQuestionList.map((q, idx) => {
                  const qid = q.question_id || `Q${idx + 1}`;
                  return (
                    <div key={qid} style={{ ...styles.card, marginTop: 8 }}>
                      <div style={styles.small}><strong>{qid}</strong></div>
                      <LnatQuestionStylePanel
                        q={q}
                        idx={idx}
                        onStemChange={(html) => updateLnatQuestionStyle(qid, { stem: html })}
                        onOptionChange={(label, html) => updateLnatQuestionStyle(qid, {
                          optionLabel: label,
                          optionHtml: html,
                        })}
                      />
                      {q.correct_label ? (
                        <div style={{ ...styles.small, marginTop: 8 }}>
                          Correct label: <strong>{q.correct_label}</strong>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                <details style={{ marginTop: 8 }}>
                  <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>
                    Questions output JSON (show/hide)
                  </summary>
                  <pre style={{ ...styles.preview, maxHeight: 260 }}>
                    {JSON.stringify(lnatQuestions, null, 2)}
                  </pre>
                </details>
              </div>
            ) : null}
            {lnatQuestionValidation ? (
              <details style={{ marginTop: 8 }}>
                <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>
                  Question validation JSON (show/hide) — {lnatQuestionValidationPassed ? 'PASS' : 'FAIL / pending'}
                </summary>
                <pre style={{ ...styles.preview, maxHeight: 220 }}>{JSON.stringify(lnatQuestionValidation, null, 2)}</pre>
              </details>
            ) : null}
            {lnatCompiled ? (
              <div style={{ marginTop: 10 }}>
                <div style={styles.small}>Compiled explanations (Step 5): {lnatCompiledQuestionList.length} questions</div>
                {lnatCompiledQuestionList.map((q, idx) => {
                  const qid = q.question_id || `Q${idx + 1}`;
                  return (
                    <div key={qid} style={{ ...styles.card, marginTop: 8 }}>
                      <div style={styles.small}>
                        <strong>{qid}</strong>
                        {' · '}
                        correct {q.correct_label || '—'}
                      </div>
                      <LnatQuestionStylePanel
                        q={q}
                        idx={idx}
                        showExplanation
                        onStemChange={(html) => updateLnatQuestionStyle(qid, { stem: html })}
                        onOptionChange={(label, html) => updateLnatQuestionStyle(qid, {
                          optionLabel: label,
                          optionHtml: html,
                        })}
                        onExplanationChange={(html) => updateLnatQuestionStyle(qid, { explanation_html: html })}
                      />
                    </div>
                  );
                })}
                <details style={{ marginTop: 8 }} open={lnatCompiledQuestionList.length === 0}>
                  <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>Step 5 output JSON (show/hide)</summary>
                  <pre style={{ ...styles.preview, maxHeight: 260 }}>{JSON.stringify(lnatCompiled, null, 2)}</pre>
                </details>
              </div>
            ) : null}
            {lnatDbQuestionList.length ? (
              <div style={{ marginTop: 10 }}>
                <div style={styles.small}>
                  Database records (Step 6): {lnatDbQuestionList.length}
                  {' · '}
                  Edit styling syncs to export and partial save.
                </div>
                {lnatDbQuestionList.map((rec, idx) => (
                  <div key={rec.external_question_id || `db-${idx}`} style={{ ...styles.card, marginTop: 8 }}>
                    <div style={styles.small}><strong>{rec.external_question_id || `Record ${idx + 1}`}</strong></div>
                    <LnatDbRecordStylePanel
                      rec={rec}
                      onQuestionTextChange={(html) => {
                        setLnatDatabase((prev) => (
                          prev
                            ? lnatMutateDatabaseRecord(prev, rec.external_question_id, (r) => ({
                              ...r,
                              question_text: html,
                            }))
                            : prev
                        ));
                      }}
                      onExplanationChange={(html) => {
                        setLnatDatabase((prev) => (
                          prev
                            ? lnatMutateDatabaseRecord(prev, rec.external_question_id, (r) => ({
                              ...r,
                              explanation: html,
                            }))
                            : prev
                        ));
                      }}
                    />
                  </div>
                ))}
                <details style={{ marginTop: 8 }}>
                  <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>Step 6 output JSON (show/hide)</summary>
                  <pre style={{ ...styles.preview, maxHeight: 260 }}>{JSON.stringify(lnatDatabase, null, 2)}</pre>
                </details>
              </div>
            ) : null}
            {lnatVerificationReports.length ? (
              <div style={{ marginTop: 10 }}>
                <div style={styles.small}>Verification reports (Step 7): {lnatVerificationReports.length}</div>
                {lnatVerificationReports.map((rep, idx) => (
                  <div key={rep.external_question_id || `step7-${idx}`} style={{ ...styles.card, marginTop: 8 }}>
                    <div style={styles.small}>
                      <strong>{rep.external_question_id || `Q${rep.slot_index || idx + 1}`}</strong>
                      {rep.final_verdict ? ` — ${rep.final_verdict}` : ''}
                    </div>
                    {rep.report_markdown ? (
                      <pre style={{ ...styles.preview, maxHeight: 320, whiteSpace: 'pre-wrap', marginTop: 8 }}>
                        {rep.report_markdown}
                      </pre>
                    ) : null}
                  </div>
                ))}
                <details style={{ marginTop: 8 }}>
                  <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>Step 7 output JSON (show/hide)</summary>
                  <pre style={{ ...styles.preview, maxHeight: 260 }}>{JSON.stringify(lnatVerification, null, 2)}</pre>
                </details>
              </div>
            ) : null}
            {lnatExaminationReports.length ? (
              <div style={{ marginTop: 10 }}>
                <div style={styles.small}>Behavioural fidelity reports (Step 8): {lnatExaminationReports.length}</div>
                {lnatExaminationReports.map((rep, idx) => (
                  <div key={rep.external_question_id || `step8-${idx}`} style={{ ...styles.card, marginTop: 8 }}>
                    <div style={styles.small}>
                      <strong>{rep.external_question_id || `Q${rep.slot_index || idx + 1}`}</strong>
                      {rep.final_verdict ? ` — ${rep.final_verdict}` : ''}
                    </div>
                    {rep.report_markdown ? (
                      <pre style={{ ...styles.preview, maxHeight: 320, whiteSpace: 'pre-wrap', marginTop: 8 }}>
                        {rep.report_markdown}
                      </pre>
                    ) : null}
                  </div>
                ))}
                <details style={{ marginTop: 8 }}>
                  <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>Step 8 output JSON (show/hide)</summary>
                  <pre style={{ ...styles.preview, maxHeight: 260 }}>{JSON.stringify(lnatExamination, null, 2)}</pre>
                </details>
              </div>
            ) : null}
          </div>
        ) : null}
        {isLnatExam ? (
          <>
            <button
              type="button"
              style={{
                ...styles.btn,
                ...styles.btnPrimary,
                marginTop: 16,
                ...(!examReaderSource.questions.length ? styles.btnDisabled : {}),
              }}
              disabled={!examReaderSource.questions.length}
              onClick={() => setExamReaderOpen((open) => !open)}
              title="Preview how exam JSON renders (passages, LaTeX, MCQ, essay choice, explanations)"
            >
              {examReaderOpen ? 'Close exam preview' : 'Exam preview'}
            </button>
            {!examReaderSource.questions.length ? (
              <p style={{ ...styles.small, marginTop: 8 }}>
                Run Compile database (Step 6) on at least one passage, or Build full exam, to preview how questions render.
              </p>
            ) : null}
            {examReaderOpen ? (
              <ExamReaderPanel
                key={`${examReaderSource.label}-${examReaderSource.questions.length}`}
                questions={examReaderSource.questions}
                sourceLabel={examReaderSource.label}
                onClose={() => setExamReaderOpen(false)}
              />
            ) : null}
          </>
        ) : null}
        {caseJson && (
          <>
            {ragPreview ? (
              <details style={{ ...styles.card, marginTop: 12 }}>
                <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 0 }}>RAG / sources (retrieved text used for the case)</summary>
                <div style={{ ...styles.small, marginTop: 8, marginBottom: 8 }}>
                  Corpus: {ragPreview.corpus_chars} chars → {ragPreview.corpus_chunk_count} chunks · Primary assembled: {ragPreview.primary_assembled_chars} chars · Supplementary: {ragPreview.research_chars} chars
                  {ragPreview.extra_links_truncated ? ' · Note: extra links list was truncated to server max.' : ''}
                </div>
                {ragPreview.upload_max_bytes ? (
                  <div style={styles.small}>
                    Upload limit: {(ragPreview.upload_max_bytes / (1024 * 1024)).toFixed(1)} MB per file · URL fetch timeout: {ragPreview.url_fetch_timeout_sec}s each
                  </div>
                ) : null}
                {Array.isArray(ragPreview.extra_link_results) && ragPreview.extra_link_results.length > 0 ? (
                  <div style={{ marginTop: 10 }}>
                    <div style={styles.small}>Extra links</div>
                    <ul style={{ ...styles.small, margin: '6px 0 0 18px', padding: 0 }}>
                      {ragPreview.extra_link_results.map((row, i) => (
                        <li key={`${row.url}-${i}`}>
                          {row.fetched_ok ? 'OK' : 'empty/failed'} ({row.text_chars} chars): {row.url}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {Array.isArray(ragPreview.file_results) && ragPreview.file_results.length > 0 ? (
                  <div style={{ marginTop: 10 }}>
                    <div style={styles.small}>Files</div>
                    <ul style={{ ...styles.small, margin: '6px 0 0 18px', padding: 0 }}>
                      {ragPreview.file_results.map((row) => (
                        <li key={row.filename}>
                          {row.ingested ? `Ingested (${row.text_chars} chars)` : `Skipped (${row.status})`}: {row.filename}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div style={{ marginTop: 12 }}>
                  <div style={styles.small}>Primary source excerpt (after ranking + assembly)</div>
                  <pre style={{ ...styles.preview, maxHeight: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ragPreview.primary_source_excerpt || '—'}</pre>
                </div>
                <div style={{ marginTop: 8 }}>
                  <div style={styles.small}>Supplementary excerpt</div>
                  <pre style={{ ...styles.preview, maxHeight: 160, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{ragPreview.supplementary_excerpt || '—'}</pre>
                </div>
              </details>
            ) : null}
            <div style={styles.card}>
              <div style={{ marginBottom: 8 }}>
                <button type="button" style={styles.btn} onClick={downloadJson}>Download JSON (DEV MODE)</button>
              </div>
              <div style={styles.sectionTitle}>Section 2 — Logline</div>
              <div style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  style={{ ...styles.btn, ...(loadingFinalLoglines ? styles.btnDisabled : {}) }}
                  disabled={loadingFinalLoglines}
                  onClick={handleFinalLoglineSuggestions}
                >
                  {loadingFinalLoglines ? 'Getting loglines...' : 'Suggest 2-3 loglines'}
                </button>
              </div>
              {finalLoglineSuggestions.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={styles.small}>Logline suggestions:</div>
                  {finalLoglineSuggestions.map((line, idx) => (
                    <button
                      key={`final-${idx}-${line.slice(0, 24)}`}
                      type="button"
                      style={styles.btn}
                      title={line}
                      onClick={() => updateRichField('descriptionHtml', plainLoglineToDescriptionHtml(line))}
                    >
                      Use: {line}
                    </button>
                  ))}
                </div>
              )}
              <RichEditor
                label="Final logline"
                value={richFields.descriptionHtml}
                onChange={(html) => updateRichField('descriptionHtml', html)}
                minHeight={90}
              />
              <p style={{ ...styles.small, marginTop: -8, marginBottom: 16 }}>
                Editable areas below use the same HTML styling as the reading preview (paragraph spacing, quote lists, Act III options inside the act body).
              </p>
              <div style={styles.sectionTitle}>Section 3 — Acts</div>
              <div style={styles.actCaseBlock}>
                <div style={styles.actCaseKicker}>Act 1</div>
                <label style={styles.label}>Act 1 title</label>
                <textarea
                  style={styles.textareaActTitle}
                  rows={1}
                  value={richFields.act1Title}
                  onChange={(e) => updateRichField('act1Title', e.target.value)}
                />
                <RichEditor
                  label="Act 1 body (HTML — case view)"
                  value={richFields.act1Html}
                  onChange={(html) => updateRichField('act1Html', html)}
                  minHeight={220}
                />
              </div>
              <div style={styles.actCaseBlock}>
                <div style={styles.actCaseKicker}>Act 2</div>
                <label style={styles.label}>Act 2 title</label>
                <textarea
                  style={styles.textareaActTitle}
                  rows={1}
                  value={richFields.act2Title}
                  onChange={(e) => updateRichField('act2Title', e.target.value)}
                />
                <RichEditor
                  label="Act 2 body (HTML — case view)"
                  value={richFields.act2Html}
                  onChange={(html) => updateRichField('act2Html', html)}
                  minHeight={220}
                />
              </div>
              <div style={styles.actCaseBlock}>
                <div style={styles.actCaseKicker}>Act 3</div>
                <label style={styles.label}>Act 3 title</label>
                <textarea
                  style={styles.textareaActTitle}
                  rows={1}
                  value={richFields.act3Title}
                  onChange={(e) => updateRichField('act3Title', e.target.value)}
                />
                <RichEditor
                  label="Act 3 body (HTML — dilemma + numbered options in one field)"
                  value={richFields.act3Html}
                  onChange={(html) => updateRichField('act3Html', html)}
                  minHeight={280}
                />
              </div>
            </div>

            <div style={styles.card}>
              <label style={styles.label}>Optional main cover prompt override</label>
              <textarea style={{ ...styles.textarea, minHeight: 90 }} value={promptOverride} onChange={(e) => setPromptOverride(e.target.value)} />
              <button type="button" style={{ ...styles.btn, ...styles.btnPrimary, ...(loadingCovers ? styles.btnDisabled : {}) }} disabled={loadingCovers} onClick={generateAllFourCovers}>
                {loadingCovers ? 'Generating cover images...' : 'Generate images'}
              </button>
              <button type="button" style={styles.btn} onClick={downloadSelectedImage} disabled={!selectedCoverId}>
                Download selected image (DEV MODE)
              </button>
              <div style={styles.small}>Select one image card below. That one is used for download and publish cover source.</div>
              <div style={styles.grid2}>
                {coverItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => selectCover(item.id)}
                    style={{
                      border: selectedCoverId === item.id ? '2px solid var(--iv-head)' : '1px solid var(--iv-line)',
                      padding: 8,
                      background: 'var(--iv-surface)',
                      cursor: 'pointer',
                    }}
                  >
                    {item.url ? <img src={item.url} alt={item.label} style={styles.img} /> : null}
                  </button>
                ))}
              </div>
              <div style={styles.sectionTitle}>Section 4 — Exhibits</div>
              <div style={{ marginBottom: 8 }}>
                <button type="button" style={styles.btn} onClick={addExhibit}>Add exhibit</button>
              </div>
              {getExhibits().map((ex, idx) => {
                const exhibitTableModel = parseExhibitTablesModel(ex.HTML_data || '');
                const tableCount = exhibitTableModel.tables.length;
                return (
                <div key={`exhibit-${idx}`} style={styles.actCaseBlock}>
                  <div style={{ ...styles.actCaseKicker, marginBottom: 12 }}>{`Exhibit ${idx + 1}`}</div>
                  <label style={styles.label}>Title</label>
                  <textarea style={styles.textareaActTitle} rows={1} value={ex.title || ''} onChange={(e) => updateExhibitAt(idx, { title: e.target.value })} />
                  <RichEditor
                    key={`exhibit-${idx}-description`}
                    label="Description (HTML — case view)"
                    value={ex.description || '<p></p>'}
                    onChange={(html) => updateExhibitAt(idx, { description: html })}
                    minHeight={120}
                  />
                  <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
                    Same toolbar as acts: bold, lists, indent/outdent. JSON stores HTML.
                  </p>
                  <div style={{ marginBottom: 6 }}>
                    <label style={styles.label}>Exhibit tables</label>
                    <p style={{ ...styles.small, marginTop: 0, marginBottom: 10 }}>
                      Add tables and edit cells here.
                    </p>
                    <div style={{ ...styles.toolbar, marginBottom: 10 }}>
                      <button
                        type="button"
                        style={styles.toolBtn}
                        onClick={() =>
                          updateExhibitAt(idx, {
                            HTML_data: serializeExhibitTablesModel(
                              exhibitModelAddTable(parseExhibitTablesModel(ex.HTML_data || '')),
                            ),
                          })
                        }
                      >
                        Add table
                      </button>
                      <button
                        type="button"
                        style={styles.toolBtn}
                        onClick={() => updateExhibitAt(idx, { HTML_data: '' })}
                        disabled={tableCount === 0}
                      >
                        Clear tables
                      </button>
                    </div>
                    {tableCount === 0 ? (
                      <p style={{ ...styles.small, marginTop: 0, marginBottom: 10 }}>No tables yet — use “Add table”.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 12 }}>
                        {exhibitTableModel.tables.map((t, ti) => (
                          <div
                            key={`exhibit-${idx}-table-${ti}`}
                            style={{
                              border: '1px solid var(--iv-line)',
                              padding: '12px 14px',
                              background: 'var(--iv-bg)',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 10,
                              }}
                            >
                              <span style={{ ...styles.small, fontWeight: 600 }}>{`Table ${ti + 1}`}</span>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                onClick={() =>
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(
                                      exhibitModelAddRow(parseExhibitTablesModel(ex.HTML_data || ''), ti),
                                    ),
                                  })
                                }
                              >
                                Add row
                              </button>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                onClick={() =>
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(
                                      exhibitModelAddColumn(parseExhibitTablesModel(ex.HTML_data || ''), ti),
                                    ),
                                  })
                                }
                              >
                                Add column
                              </button>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                onClick={() =>
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(
                                      exhibitModelRemoveLastColumn(parseExhibitTablesModel(ex.HTML_data || ''), ti),
                                    ),
                                  })
                                }
                              >
                                Remove last column
                              </button>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                disabled={(t.rows || []).length <= 1}
                                onClick={() =>
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(
                                      exhibitModelRemoveLastRow(parseExhibitTablesModel(ex.HTML_data || ''), ti),
                                    ),
                                  })
                                }
                              >
                                Remove last row
                              </button>
                              <button
                                type="button"
                                style={styles.toolBtn}
                                onClick={() => {
                                  const next = exhibitModelRemoveTable(parseExhibitTablesModel(ex.HTML_data || ''), ti);
                                  updateExhibitAt(idx, {
                                    HTML_data: serializeExhibitTablesModel(next),
                                  });
                                }}
                              >
                                Delete this table
                              </button>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                              <table className="studio-exhibit-table-grid" style={exhibitStudioTableGrid}>
                                <thead>
                                  <tr>
                                    {t.headers.map((h, ci) => (
                                      <th key={`h-${ti}-${ci}`} style={{ ...exhibitStudioThCell, minWidth: 96 }}>
                                        <input
                                          style={exhibitStudioHeaderCellInput}
                                          value={h}
                                          aria-label={`Table ${ti + 1} header ${ci + 1}`}
                                          onChange={(e) =>
                                            updateExhibitAt(idx, {
                                              HTML_data: serializeExhibitTablesModel(
                                                exhibitModelSetHeader(
                                                  parseExhibitTablesModel(ex.HTML_data || ''),
                                                  ti,
                                                  ci,
                                                  e.target.value,
                                                ),
                                              ),
                                            })
                                          }
                                        />
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {t.rows.map((row, ri) => (
                                    <tr key={`r-${ti}-${ri}`}>
                                      {row.map((cell, ci) => (
                                        <td key={`c-${ti}-${ri}-${ci}`} style={exhibitStudioTdCell}>
                                          <input
                                            style={exhibitStudioCellInput}
                                            value={cell}
                                            aria-label={`Table ${ti + 1} row ${ri + 1} column ${ci + 1}`}
                                            onChange={(e) =>
                                              updateExhibitAt(idx, {
                                                HTML_data: serializeExhibitTablesModel(
                                                  exhibitModelSetCell(
                                                    parseExhibitTablesModel(ex.HTML_data || ''),
                                                    ti,
                                                    ri,
                                                    ci,
                                                    e.target.value,
                                                  ),
                                                ),
                                              })
                                            }
                                          />
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <label style={styles.label}>Exhibit image URL (read-only; filled from Spaces after publish)</label>
                  <div style={{ ...styles.small, marginBottom: 8, wordBreak: 'break-all' }}>
                    {(ex.image_url || '').trim().startsWith('http') ? ex.image_url : '—'}
                  </div>
                  <RichEditor
                    key={`exhibit-${idx}-notes`}
                    label="Notes (HTML — case view)"
                    value={ex.notes || '<p></p>'}
                    onChange={(html) => updateExhibitAt(idx, { notes: html || '<p></p>' })}
                    minHeight={88}
                  />
                  <RichEditor
                    key={`exhibit-${idx}-source`}
                    label="Source (HTML — case view)"
                    value={ex.source || '<p></p>'}
                    onChange={(html) => updateExhibitAt(idx, { source: html || '<p></p>' })}
                    minHeight={80}
                  />
                  <label style={styles.label}>Exhibit image (optional)</label>
                  <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
                    Add image file you want to include in exhibit
                  </p>
                  <input
                    style={{ ...styles.input, padding: 8 }}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                    multiple
                    onChange={(e) => {
                      const arr = Array.from(e.target.files || []);
                      setExhibitAssets((prev) => ({
                        ...prev,
                        [idx]: {
                          ...(prev[idx] || {}),
                          imageFiles: arr,
                        },
                      }));
                    }}
                  />
                  <label style={styles.label}>Enhance exhibit source URL (optional)</label>
                  <input style={styles.input} placeholder="https://... (used for exhibit enhancement)" value={exhibitAssets[idx]?.sourceUrl || ''} onChange={(e) => setExhibitAssets((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), sourceUrl: e.target.value } }))} />
                  <label style={styles.label}>Enhance exhibit source file (optional)</label>
                  <input
                    style={{ ...styles.input, padding: 8 }}
                    type="file"
                    accept=".pdf,.txt,.md,.csv,.json,application/pdf,text/plain"
                    onChange={(e) => {
                      const file = (e.target.files && e.target.files[0]) || null;
                      setExhibitAssets((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), sourceFile: file } }));
                    }}
                  />
                  <div>
                    <button type="button" style={{ ...styles.btn, ...(enhancingExhibitIndex === idx ? styles.btnDisabled : {}) }} disabled={enhancingExhibitIndex === idx} onClick={() => enhanceExhibit(idx)}>
                      {enhancingExhibitIndex === idx ? 'Enhancing…' : 'Enhance exhibit from link/file'}
                    </button>
                    <button type="button" style={styles.btn} onClick={() => deleteExhibit(idx)}>Delete exhibit</button>
                  </div>
                </div>
                );
              })}
            </div>

            <details open style={{ marginTop: 12 }}>
              <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 8, userSelect: 'none' }}>
                Full reading preview — HTML rendered from JSON fields (description, act_*.content) (dev mode)
              </summary>
              <p style={{ ...styles.small, marginBottom: 12 }}>
                This is not raw JSON: the browser renders the HTML strings stored inside the JSON (e.g. &lt;p&gt;, &lt;ul&gt;&lt;li&gt; quotes, &lt;ol&gt;&lt;li&gt;&lt;strong&gt;1.&lt;/strong&gt; options). New cases should get this structure from generation; edit below if needed.
              </p>
              <CaseReadingPreview caseJson={caseJson} />
            </details>
            <details style={{ marginTop: 8 }}>
              <summary style={{ ...styles.label, cursor: 'pointer', marginBottom: 8, userSelect: 'none' }}>
                Raw JSON (entire case object) (DEV MODE)
              </summary>
              <pre style={styles.jsonPre}>{JSON.stringify(caseJson, null, 2)}</pre>
            </details>

            <div style={{ ...styles.card, marginTop: 20 }}>
              <div style={{ ...styles.sectionTitle, ...styles.sectionTitleFirst }}>Publish</div>
              <p style={{ ...styles.small, marginTop: 0, marginBottom: 8 }}>
                Select one generated image above, or upload a casecover image below.
              </p>
              <label style={styles.label}>Case cover image (single file)</label>
              <p style={{ ...styles.small, marginTop: -8, marginBottom: 8 }}>
                PNG, JPEG, or WebP.
              </p>
              <input
                style={{ ...styles.input, padding: 8 }}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                onChange={(e) => {
                  const f = e.target.files && e.target.files[0];
                  setPublishHeroFile(f || null);
                  if (f) setSelectedCoverId('');
                  setPublishResult(null);
                  setPublishPreviewFailed(false);
                  if (f) {
                    setPublishPreviewLoading(true);
                    setPublishHeroPreviews(null);
                  } else {
                    setPublishPreviewLoading(false);
                    setPublishHeroPreviews(null);
                  }
                }}
              />
              {publishPreviewLoading ? (
                <p style={{ ...styles.small, marginTop: 10 }}>Building previews…</p>
              ) : null}
              {publishHeroPreviews && publishHeroPreviews.length > 0 ? (
                <div style={{ marginTop: 14 }}>
                  <div style={styles.label}>How the three uploads will look (centre crop, exact pixels)</div>
                  <p style={{ ...styles.small, marginTop: 4, marginBottom: 10 }}>
                    Same geometry as the server; only PNG compression may differ slightly. The 1920×1080 tile is scaled down here for the page.
                  </p>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                      gap: 12,
                      alignItems: 'start',
                    }}
                  >
                    {publishHeroPreviews.map((row) => (
                      <div
                        key={row.key}
                        style={{
                          border: '1px solid var(--iv-line)',
                          padding: 8,
                          background: 'var(--iv-surface)',
                        }}
                      >
                        <div style={{ ...styles.small, marginBottom: 6, fontWeight: 600 }}>
                          {row.w}×{row.h}
                        </div>
                        <img
                          src={row.src}
                          alt={`Preview ${row.key}`}
                          style={{
                            width: '100%',
                            maxWidth: row.w >= 1000 ? 320 : '100%',
                            height: 'auto',
                            display: 'block',
                            border: '1px solid var(--iv-line)',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {publishPreviewFailed ? (
                <p style={{ ...styles.small, marginTop: 10, color: 'var(--iv-muted)' }}>
                  Could not build previews for this file. If it is a valid PNG, JPEG, or WebP, you can still try Publish.
                </p>
              ) : null}
              {publishResult ? (
                <div style={{ ...styles.ok, marginTop: 12 }}>
                  Published. Case ID: {publishResult.case_study_id}
                  {'\n'}Slug: {publishResult.slug}
                  {'\n'}Small: {publishResult.image_urls?.image270x380}
                  {'\n'}Medium: {publishResult.image_urls?.image835x420}
                  {'\n'}Large: {publishResult.image_urls?.image1920x1080}
                </div>
              ) : null}
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  style={{
                    ...styles.btn,
                    ...styles.btnPrimary,
                    ...((publishing || (!publishHeroFile && !selectedCoverForPublish?.url)) ? styles.btnDisabled : {}),
                  }}
                  disabled={publishing || (!publishHeroFile && !selectedCoverForPublish?.url)}
                  onClick={handlePublishCaseAndMedia}
                >
                  {publishing ? 'Publishing…' : 'Publish'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExamStudioPage;
